import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaEye } from 'react-icons/fa';
import { MdEdit } from 'react-icons/md';
import { Link, useSearchParams } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import TablePagination from './TablePagination';
import { ApiLoadState, ApiLoadTableRow } from './ApiLoadState';
import { DEFAULT_PAGE_SIZE, getServerPagination } from '../../utils/pagination';
import { SearchableSelect } from './SearchableSelect';
import { useAuth } from '../../context/AuthContext';
import {
  useCreateComplaintMutation,
  useGetActiveCategoriesQuery,
  useGetActiveComplaintLocationsQuery,
  useGetActiveSubcategoriesByCategoryQuery,
  useGetIssueTypesQuery,
  useGetAccountableDepartmentsQuery,
  useGetComplaintRaisedByListQuery,
  useGetWorkPrioritiesQuery,
  useGetComplaintsQuery,
  useUpdateComplaintMutation,
  useCreateComplaintCommentMutation,
  useCreateComplaintRatingMutation,
} from '../../redux/complaintApi';
import { ComplaintStarRating } from '../ComplaintStarRating';
import {
  complaintRatingFromRaw,
  complaintStatusAllowsEmployeeRating,
} from '../../utils/complaintRating';
import {
  COMPLAINT_STATUS_SELECT_OPTIONS,
  canAccountEditComplaint,
  complaintEverResolvedOrArchived,
  complaintStatusForPayload,
  complaintStatusSelectValue,
  formatComplaintStatusLabel,
  getComplaintStatusBadgeClass,
  isComplaintRaisedByAccount,
} from '../../utils/complaintStatus';
import {
  latestTransferredToDepartmentId,
  latestTransferredToDepartmentName,
  transferSourceDepartmentId,
} from '../../utils/complaintResolution';
import { toast } from 'react-toastify';
import {
  COMPLAINT_ATTACHMENT_MAX_FILES,
  COMPLAINT_QUANTITY_NA,
  COMPLAINT_REMARKS_MAX_LEN,
  COMPLAINT_VIDEO_MAX_BYTES,
  IMAGE_MAX_KB,
  complaintQuantityToFormValue,
  complaintQuantityToPayload,
  complaintUomToFormValue,
  formatComplaintQuantity,
  formatComplaintUom,
  validateComplaintMediaFiles,
  validateCtmComplaintForm,
  validateItsmComplaintForm,
} from '../../utils/validation';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { AuthorizedMedia } from '../AuthorizedMedia';
import {
  COMPLAINT_LIST_STATUS_OPTIONS,
  apiFiltersForComplaintStatus,
  isValidComplaintStatus,
  readComplaintFilterFromSearchParams,
  applyComplaintStatusToSearchParams,
} from '../../utils/complaintListFilters';
import {
  useGetLocationByReportingSupervisorComplaintQuery,
  useGetEmployeesQuery,
  useGetEmployeeByIdQuery,
} from '../../redux/employeeApi';
import { commentedByTypeFromAccountPayload } from '../../utils/accountComplaintComment';
import { inferCommentedByType } from '../../utils/accountRoleClassification';
import {
  complaintTatDisplayValue,
  formatReportedDateTime,
} from './backendAssignedComplaintRows';
import { ComplaintTrackingModal } from './ComplaintTrackingModal';
import { TrackingStatusButton } from './TrackingStatusButton';
import { useGetProductsQuery } from '../../redux/productApi';
import { useGetProductCategoriesQuery } from '../../redux/productCategoryApi';
import { useGetProductSubcategoriesQuery } from '../../redux/productSubcategoryApi';

const complaintStatusSelectClasses =
  'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

const getStatusClasses = (status) => getComplaintStatusBadgeClass(status);

/** Account id for dropdown + `account_id` query param (`account_id` or list row `id`). */
const complaintRaisedByAccountId = (row) => {
  if (!row || typeof row !== 'object') return '';
  const accountId = row.account_id ?? row.id;
  if (accountId != null && String(accountId).trim() !== '') return String(accountId).trim();
  return '';
};

const complaintRaisedByRowLabel = (row) => {
  const label =
    row?.name ??
    row?.employee_name ??
    row?.account_name ??
    row?.raised_by_name ??
    row?.complaint_raised_by_name;
  if (label != null && String(label).trim() !== '') return String(label).trim();
  const id = complaintRaisedByAccountId(row);
  return id !== '' ? id : '';
};

const findComplaintRaisedByRowByAccountId = (rows, accountId) => {
  if (accountId == null || String(accountId) === '') return null;
  return (Array.isArray(rows) ? rows : []).find(
    (row) => String(complaintRaisedByAccountId(row)) === String(accountId)
  );
};

const fieldInputClasses =
  'mt-1 w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm text-gray-900 placeholder:text-gray-600 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

const resolutionFieldDisabledClasses =
  'cursor-not-allowed bg-gray-100 text-gray-500 opacity-90';

const fieldTextareaClasses =
  'mt-1 w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm text-gray-900 placeholder:text-gray-600 min-h-[120px] focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand';

const reqLabel = (text) => (
  <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
    <span className="min-w-0 leading-snug">{text}</span>
    <span className="shrink-0 text-base leading-none text-red-600" aria-hidden="true">
      *
    </span>
  </span>
);

const fieldErrorTextClass = 'mt-1 text-xs text-red-600';

const inputErrorClasses = 'border-red-500 ring-1 ring-red-500';

const isVideoUrl = (url, contentType) => {
  const ct = String(contentType || '').toLowerCase();
  if (ct.startsWith('video/')) return true;
  return /\.(mp4|webm|ogg|mov|m4v|mkv)(\?|$)/i.test(String(url || ''));
};

const mapRawAttachmentToExisting = (a) => {
  const id = a?.id ?? a?.attachment_id;
  const url = resolveAssetUrl(
    a?.url || a?.file_url || a?.signed_url || a?.blob_url || a?.image_url || ''
  );
  if (!url) return null;
  return {
    id: id != null && String(id) !== '' ? String(id) : null,
    url,
    contentType: a?.content_type || a?.contentType || '',
    filename: String(a?.filename || a?.file_name || a?.name || '').trim(),
  };
};

/** Prefer non-empty list; API may send `attachments_url` instead of `attachments`. */
const attachmentListFromRaw = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  if (Array.isArray(raw.attachments) && raw.attachments.length > 0) return raw.attachments;
  if (Array.isArray(raw.attachments_url) && raw.attachments_url.length > 0)
    return raw.attachments_url;
  if (Array.isArray(raw.attachment_urls) && raw.attachment_urls.length > 0)
    return raw.attachment_urls;
  if (Array.isArray(raw.attachments)) return raw.attachments;
  if (Array.isArray(raw.attachments_url)) return raw.attachments_url;
  return null;
};

const parseExistingAttachmentsFromRaw = (raw) => {
  const out = [];
  if (!raw || typeof raw !== 'object') return out;
  const list = attachmentListFromRaw(raw);
  if (list && list.length > 0) {
    list.forEach((a) => {
      const row = mapRawAttachmentToExisting(a);
      if (row) out.push(row);
    });
    return out;
  }
  const legacy = resolveAssetUrl(raw.image_url || raw.imageUrl || '');
  if (legacy) {
    const legacyId = raw?.image_attachment_id ?? raw?.attachment_id;
    out.push({
      id: legacyId != null && String(legacyId) !== '' ? String(legacyId) : null,
      url: legacy,
      contentType: '',
      filename: '',
    });
  }
  return out;
};

const attachmentUrlsFromRaw = (row) => {
  if (!row || typeof row !== 'object') return [];
  const list = attachmentListFromRaw(row);
  if (list && list.length > 0) {
    return list
      .map((a) => resolveAssetUrl(a?.url || a?.file_url || a?.signed_url || ''))
      .filter(Boolean);
  }
  const u = resolveAssetUrl(row.image_url || row.imageUrl || '');
  return u ? [u] : [];
};

/** Full-width gallery for complaint detail drawer (images + videos + filenames). */
const ComplaintDetailAttachmentGallery = ({ items }) => {
  if (!Array.isArray(items) || items.length === 0) {
    return <p className="text-sm text-gray-500">No attachments uploaded.</p>;
  }
  return (
    <div className="flex flex-wrap gap-3">
      {items.map((a, idx) => {
        const key = `${a.id ?? 'x'}-${idx}-${a.url}`;
        const vid = isVideoUrl(a.url, a.contentType);
        return (
          <div key={key} className="flex w-32 max-w-[40%] flex-shrink-0 flex-col gap-1 sm:max-w-none">
            <div className="relative aspect-square w-full overflow-hidden rounded-lg border border-gray-200 bg-black/5">
              {vid ? (
                <AuthorizedMedia
                  as="video"
                  src={a.url}
                  className="h-full w-full object-cover"
                  muted
                  playsInline
                  videoControls
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <AuthorizedMedia
                  src={a.url}
                  alt={a.filename || `Attachment ${idx + 1}`}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
            </div>
            {a.filename ? (
              <p
                className="line-clamp-2 break-all text-[11px] leading-snug text-gray-600"
                title={a.filename}
              >
                {a.filename}
              </p>
            ) : (
              <p className="text-[11px] text-gray-400">{vid ? 'Video' : 'Image'}</p>
            )}
          </div>
        );
      })}
    </div>
  );
};

const complaintCommentsFromRaw = (raw) => {
  const list = raw?.complaint_comments_details;
  if (!Array.isArray(list)) return [];
  return [...list].sort((a, b) => {
    const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
    return ta - tb;
  });
};

/** Normalize API value (string or small object) for one-line display. */
const formatRcaPaCaField = (v) => {
  if (v == null) return null;
  if (typeof v === 'string') {
    const t = v.trim();
    return t || null;
  }
  if (typeof v === 'object') {
    if (v.text != null && String(v.text).trim()) return String(v.text).trim();
    if (v.name != null && String(v.name).trim()) return String(v.name).trim();
    if (v.label != null && String(v.label).trim()) return String(v.label).trim();
    if (v.status != null && String(v.status).trim()) return String(v.status).trim();
    const keys = Object.keys(v);
    if (keys.length === 0) return null;
  }
  const s = String(v).trim();
  return s || null;
};

/** Latest non-null RCA / PA / CA from `complaint_comments_details` (chronological order). */
const latestRcaPaCaFromRaw = (raw) => {
  const list = complaintCommentsFromRaw(raw);
  let rca = null;
  let pa = null;
  let ca = null;
  for (const c of list) {
    const nextRca = formatRcaPaCaField(c?.rca);
    const nextPa = formatRcaPaCaField(c?.pa);
    const nextCa = formatRcaPaCaField(c?.ca);
    if (nextRca != null) rca = nextRca;
    if (nextPa != null) pa = nextPa;
    if (nextCa != null) ca = nextCa;
  }
  return { rca, pa, ca };
};

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Convert API date/datetime into an <input type="date" /> value (YYYY-MM-DD) in local time.
 * - Accepts ISO strings, Rails timestamps, Date objects, or already-YYYY-MM-DD strings.
 */
const toDateInputValue = (value) => {
  if (!value) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`;
  }
  const s = String(value).trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // If it's an ISO datetime (or similar), prefer the literal date portion to avoid timezone shifts.
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const productCategoryIdFromRow = (row) =>
  row?.product_category_id ??
  row?.productCategoryId ??
  row?.category_id ??
  row?.categoryId ??
  '';

const productSubcategoryIdFromRow = (row) =>
  row?.product_subcategory_id ??
  row?.productSubcategoryId ??
  row?.subcategory_id ??
  row?.subcategoryId ??
  '';

const productNameFromRow = (row) =>
  row?.item_name || row?.name || row?.itemName || row?.product_name || '';

const createEmptyComplaintForm = () => ({
  location: '',
  managerOnDuty: '',
  managerOnDutyName: '',
  raisedBy: '',
  reportedDate: '',
  category: '',
  quantity: '',
  uom: '',
  productId: '',
  productName: '',
  productCategoryId: '',
  productSubcategoryId: '',
  subCategory: '',
  batchNo: '',
  mfgDate: '',
  bestBefore: '',
  department: '',
  priority: '',
  vehicle_storage_temperature: '',
  issueType: '',
  rmType: '',
  equipment: '',
  building: '',
  attachmentFiles: [],
  existingAttachments: [],
  removeAttachmentIds: [],
  issueDescription: '',
  incidentDate: '',
  remarks: '',
});

function ComplaintsPage() {
  const { area, user, canAction } = useAuth();
  const canView = canAction('complaints', 'view');
  const canCreate = canAction('complaints', 'create');
  const canEdit = canAction('complaints', 'edit');
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = useMemo(
    () => readComplaintFilterFromSearchParams(searchParams),
    [searchParams]
  );
  const [filters, setFilters] = useState({ search: '' });
  const [page, setPage] = useState(1);
  const complaintApiFilter = useMemo(
    () => ({
      filter: apiFiltersForComplaintStatus(statusFilter)[0] || undefined,
      page,
      per_page: DEFAULT_PAGE_SIZE,
      search: filters.search?.trim() || undefined,
    }),
    [statusFilter, page, filters.search]
  );

  const [formData, setFormData] = useState(() => createEmptyComplaintForm());
  const isAdmin = area === 'admin';
  const isEmployeeArea = area === 'employee';
  const [activeTab, setActiveTab] = useState('CTM'); // ITSM | CTM
  const [createComplaint, { isLoading: isCreatingComplaint }] = useCreateComplaintMutation();
  const [updateComplaint, { isLoading: isUpdatingComplaint }] = useUpdateComplaintMutation();
  const [createComplaintComment, { isLoading: isSavingComplaintComment }] =
    useCreateComplaintCommentMutation();
  const [createComplaintRating, { isLoading: isSavingComplaintRating }] =
    useCreateComplaintRatingMutation();
  const loggedInAccountId = user?.id != null ? String(user.id).trim() : '';
  const {
    data: loggedInAccountRes,
    isFetching: isLoadingCommentAuthorType,
  } = useGetEmployeeByIdQuery(loggedInAccountId, { skip: !loggedInAccountId });
  const commentedByTypeForComments = useMemo(() => {
    const fromAccount = commentedByTypeFromAccountPayload(loggedInAccountRes);
    if (fromAccount) return fromAccount;
    return inferCommentedByType({
      roleName: user?.roleName,
    });
  }, [loggedInAccountRes, user]);
  const { data: locationsRes } = useGetActiveComplaintLocationsQuery();
  const { data: employeesRes } = useGetEmployeesQuery(undefined, { skip: !isAdmin });
  const { data: complaintRaisedByListRes } = useGetComplaintRaisedByListQuery(formData.location, {
    skip: !formData.location,
  });
  const { data: categoriesRes } = useGetActiveCategoriesQuery(
    { accountable_department_id: formData.department },
    { skip: !formData.department }
  );
  const { data: workPrioritiesRes } = useGetWorkPrioritiesQuery();
  const { data: issueTypesRes } = useGetIssueTypesQuery();
  const { data: accountableDepartmentsListRes } = useGetAccountableDepartmentsQuery({
    complaint_type: activeTab,
  });
  const subcategoryIdForIssueTypes = formData.subCategory
    ? Number(formData.subCategory)
    : NaN;
  /** Issue types from /api/issue_types?subcategory_id=… (ITSM + CTM). */
  const { data: issueTypesBySubcategoryRes } = useGetIssueTypesQuery(
    {
      subcategory_id: Number.isFinite(subcategoryIdForIssueTypes)
        ? subcategoryIdForIssueTypes
        : undefined,
    },
    {
      skip: !Number.isFinite(subcategoryIdForIssueTypes),
    }
  );

  const issueTypeOptionsBySubcategory = useMemo(() => {
    const list = issueTypesBySubcategoryRes?.data || issueTypesBySubcategoryRes || [];
    return (Array.isArray(list) ? list : []).map((item) => ({
      id: String(item.id),
      name: item.name ?? item.issue_type_name ?? String(item.id),
    }));
  }, [issueTypesBySubcategoryRes]);

  const allAccountableDepartmentOptions = (() => {
    const seen = new Set();
    const out = [];
    for (const d of accountableDepartmentsListRes?.data || []) {
      const rawId = d?.id;
      if (rawId === '' || rawId === undefined || rawId === null) continue;
      const key = String(rawId);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        id: key,
        name: d?.name ?? `Department ${rawId}`,
      });
    }
    return out;
  })();

  /** Department dropdown options come from master department list. */
  const itsmDepartmentSelectOptions = allAccountableDepartmentOptions;

  const {
    data: complaintsRes,
    isLoading: isComplaintsLoading,
    isFetching: isComplaintsFetching,
    error: complaintsError,
    refetch: refetchComplaints,
  } = useGetComplaintsQuery(complaintApiFilter);
  const complaintAttachmentInputRef = useRef(null);
  const [newAttachmentPreviewUrls, setNewAttachmentPreviewUrls] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // list | form | detail
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const shouldLoadProductOptions = viewMode === 'form' && activeTab === 'CTM';
  const { data: productsRes } = useGetProductsQuery(
    { status: true, per_page: 500 },
    { skip: !shouldLoadProductOptions }
  );
  const { data: productCategoriesRes } = useGetProductCategoriesQuery(
    { status: true, per_page: 500 },
    { skip: !shouldLoadProductOptions }
  );
  const { data: productSubcategoriesRes } = useGetProductSubcategoriesQuery(
    {
      product_category_id: formData.productCategoryId || undefined,
      status: true,
      per_page: 500,
    },
    { skip: !shouldLoadProductOptions || !formData.productCategoryId }
  );
  const productList = useMemo(() => {
    const list = productsRes?.data || productsRes || [];
    return Array.isArray(list) ? list : [];
  }, [productsRes]);

  const productSelectOptions = useMemo(() => {
    const base = productList
      .slice()
      .sort((a, b) => Number(a.id) - Number(b.id))
      .map((product) => ({
        value: String(product.id),
        label:
          product.item_name ||
          product.name ||
          product.itemName ||
          `Product ${product.id}`,
      }));
    if (!formData.productId) return base;
    if (base.some((option) => option.value === String(formData.productId))) return base;
    const fallbackLabel =
      selectedComplaint?.raw?.complaint_product_name ||
      selectedComplaint?.raw?.product_name ||
      selectedComplaint?.raw?.productName ||
      `Product ${formData.productId}`;
    return [...base, { value: String(formData.productId), label: fallbackLabel }];
  }, [productList, formData.productId, selectedComplaint?.raw]);
  const productCategorySelectOptions = useMemo(() => {
    const base = (productCategoriesRes?.data || []).map((category) => ({
      value: String(category.id),
      label: category.name ?? String(category.id),
    }));
    if (!formData.productCategoryId) return base;
    if (base.some((option) => option.value === String(formData.productCategoryId))) return base;
    const fallbackLabel =
      selectedComplaint?.raw?.product_category_name ||
      selectedComplaint?.raw?.productCategoryName ||
      `Category ${formData.productCategoryId}`;
    return [...base, { value: String(formData.productCategoryId), label: fallbackLabel }];
  }, [productCategoriesRes, formData.productCategoryId, selectedComplaint?.raw]);
  const productSubcategorySelectOptions = useMemo(() => {
    const base = (productSubcategoriesRes?.data || []).map((subcategory) => ({
      value: String(subcategory.id),
      label: subcategory.name ?? String(subcategory.id),
    }));
    if (!formData.productSubcategoryId) return base;
    if (base.some((option) => option.value === String(formData.productSubcategoryId))) return base;
    const fallbackLabel =
      selectedComplaint?.raw?.product_subcategory_name ||
      selectedComplaint?.raw?.productSubcategoryName ||
      `Sub Category ${formData.productSubcategoryId}`;
    return [...base, { value: String(formData.productSubcategoryId), label: fallbackLabel }];
  }, [productSubcategoriesRes, formData.productSubcategoryId, selectedComplaint?.raw]);
  const [editingComplaintId, setEditingComplaintId] = useState(null);
  const [raisedBySelectOption, setRaisedBySelectOption] = useState(null);
  const [ctmFieldErrors, setCtmFieldErrors] = useState({});
  const [detailReplyComment, setDetailReplyComment] = useState('');
  const [detailReplyRca, setDetailReplyRca] = useState('');
  const [detailReplyPa, setDetailReplyPa] = useState('');
  const [detailReplyCa, setDetailReplyCa] = useState('');
  const [resolutionStatus, setResolutionStatus] = useState('');
  const [resolutionRca, setResolutionRca] = useState('');
  const [resolutionPa, setResolutionPa] = useState('');
  const [resolutionCa, setResolutionCa] = useState('');
  const [resolutionToDepartment, setResolutionToDepartment] = useState('');
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isTrackingOpen, setIsTrackingOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewFeedback, setReviewFeedback] = useState('');
  const [submittedDetailRating, setSubmittedDetailRating] = useState(null);

  const resetDetailReplyForm = () => {
    setDetailReplyComment('');
    setDetailReplyRca('');
    setDetailReplyPa('');
    setDetailReplyCa('');
  };

  const resetResolutionFields = () => {
    setResolutionStatus('');
    setResolutionRca('');
    setResolutionPa('');
    setResolutionCa('');
    setResolutionToDepartment('');
  };

  const resetReviewForm = () => {
    setIsReviewModalOpen(false);
    setReviewRating(0);
    setReviewFeedback('');
    setSubmittedDetailRating(null);
  };

  const resetTrackingModal = () => setIsTrackingOpen(false);

  useEffect(() => {
    setPage(1);
  }, [searchParams]);

  /**
   * When user finishes editing and later opens a fresh "Add Complaint" flow,
   * ensure we never carry over stale state if the component stays mounted.
   */
  useEffect(() => {
    if (viewMode !== 'form') return;
    if (editingComplaintId) return; 
    if (selectedComplaint) return; 
    setFormData(createEmptyComplaintForm());
    resetComplaintAttachmentInput();
    setRaisedBySelectOption(null);
    setCtmFieldErrors({});
    resetResolutionFields();
  }, [viewMode, editingComplaintId, selectedComplaint]);

  useEffect(() => {
    if (!formData.productId) return;
    const product = productList.find(
      (row) => String(row.id) === String(formData.productId)
    );
    if (!product) return;
    const nextCategoryId = productCategoryIdFromRow(product);
    const nextSubcategoryId = productSubcategoryIdFromRow(product);
    const nextProductName = productNameFromRow(product);
    const needsCategory = !formData.productCategoryId && nextCategoryId;
    const needsSubcategory = !formData.productSubcategoryId && nextSubcategoryId;
    const needsName = !formData.productName && nextProductName;
    if (!needsCategory && !needsSubcategory && !needsName) return;
    setFormData((prev) => ({
      ...prev,
      productCategoryId: prev.productCategoryId || (nextCategoryId ? String(nextCategoryId) : ''),
      productSubcategoryId:
        prev.productSubcategoryId || (nextSubcategoryId ? String(nextSubcategoryId) : ''),
      productName: prev.productName || nextProductName,
    }));
  }, [
    formData.productId,
    formData.productCategoryId,
    formData.productSubcategoryId,
    formData.productName,
    productList,
  ]);

  const { data: subcategoriesRes } = useGetActiveSubcategoriesByCategoryQuery(formData.category, {
    skip: !formData.category,
  });
  const complaintRaisedByRows = useMemo(() => {
    const rows = complaintRaisedByListRes?.data;
    return Array.isArray(rows) ? rows : [];
  }, [complaintRaisedByListRes?.data]);

  const raisedByAccountId = useMemo(() => {
    if (formData.raisedBy == null || String(formData.raisedBy) === '') return '';
    const row = findComplaintRaisedByRowByAccountId(complaintRaisedByRows, formData.raisedBy);
    if (row) return complaintRaisedByAccountId(row);
    return String(formData.raisedBy);
  }, [formData.raisedBy, complaintRaisedByRows]);

  const { data: reportingSupervisorsByAccountRes, isFetching: isFetchingManagerOnDuty } =
    useGetLocationByReportingSupervisorComplaintQuery(
      {
        location_id: formData.location,
        account_id: raisedByAccountId,
      },
      { skip: !formData.location || !raisedByAccountId }
    );

  const locationSelectOptions = useMemo(() => {
    const options = (locationsRes?.data || []).map((loc) => ({
      value: String(loc.id),
      label: loc.name ?? String(loc.id),
    }));
    const locationId = formData.location;
    if (locationId == null || String(locationId) === '') return options;
    if (options.some((o) => String(o.value) === String(locationId))) return options;

    const raw = selectedComplaint?.raw;
    const apiName = String(
      raw?.location_name ?? raw?.locationName ?? selectedComplaint?.location ?? ''
    ).trim();
    const label = apiName && apiName !== '—' ? apiName : String(locationId);
    return [{ value: String(locationId), label }, ...options];
  }, [locationsRes?.data, formData.location, selectedComplaint]);

  const raisedByOptions = useMemo(() => {
    const list = [];
    if (raisedBySelectOption?.value) list.push(raisedBySelectOption);

    for (const row of complaintRaisedByRows) {
      const value = complaintRaisedByAccountId(row);
      if (value === '') continue;
      list.push({
        value,
        label: complaintRaisedByRowLabel(row),
      });
    }

    const seen = new Set();
    return list.filter((o) => {
      const v = String(o.value);
      if (seen.has(v)) return false;
      seen.add(v);
      return true;
    });
  }, [complaintRaisedByRows, raisedBySelectOption]);

  const reportingSupervisorsForRaisedBy = useMemo(() => {
    if (!formData.location || !raisedByAccountId) return [];
    const rows = reportingSupervisorsByAccountRes?.data;
    return Array.isArray(rows) ? rows : [];
  }, [formData.location, raisedByAccountId, reportingSupervisorsByAccountRes?.data]);

  const managerOnDutyDisplayName = useMemo(() => {
    const savedName = String(formData.managerOnDutyName || '').trim();
    if (!formData.location || !raisedByAccountId) return savedName;
    if (reportingSupervisorsForRaisedBy.length) {
      const match = reportingSupervisorsForRaisedBy.find(
        (x) => String(x.id) === String(formData.managerOnDuty)
      );
      const row = match ?? reportingSupervisorsForRaisedBy[0];
      if (row?.name != null && String(row.name).trim() !== '') return String(row.name).trim();
    }
    return savedName;
  }, [
    formData.location,
    formData.managerOnDutyName,
    raisedByAccountId,
    reportingSupervisorsForRaisedBy,
    formData.managerOnDuty,
  ]);

  useEffect(() => {
    if (!formData.location || !raisedByAccountId) {
      if (!editingComplaintId) {
        setFormData((prev) =>
          prev.managerOnDuty || prev.managerOnDutyName
            ? { ...prev, managerOnDuty: '', managerOnDutyName: '' }
            : prev
        );
      }
      return;
    }
    if (isFetchingManagerOnDuty) return;
    if (!reportingSupervisorsForRaisedBy.length) {
      if (editingComplaintId && (formData.managerOnDuty || formData.managerOnDutyName)) return;
      setFormData((prev) =>
        prev.managerOnDuty ? { ...prev, managerOnDuty: '' } : prev
      );
      return;
    }
    if (editingComplaintId && formData.managerOnDuty) {
      const match = reportingSupervisorsForRaisedBy.find(
        (x) => String(x.id) === String(formData.managerOnDuty)
      );
      if (match?.name) {
        const nextName = String(match.name).trim();
        if (nextName && nextName !== formData.managerOnDutyName) {
          setFormData((prev) => ({ ...prev, managerOnDutyName: nextName }));
        }
      }
      return;
    }
    const match = reportingSupervisorsForRaisedBy.find(
      (x) => String(x.id) === String(formData.managerOnDuty)
    );
    const row = match ?? reportingSupervisorsForRaisedBy[0];
    const id = row?.id;
    const name = row?.name != null ? String(row.name).trim() : '';
    if (id == null || id === '' || !name) {
      setFormData((prev) =>
        prev.managerOnDuty ? { ...prev, managerOnDuty: '', managerOnDutyName: '' } : prev
      );
      return;
    }
    setFormData((prev) => {
      if (String(prev.managerOnDuty) === String(id) && prev.managerOnDutyName === name) return prev;
      return { ...prev, managerOnDuty: String(id), managerOnDutyName: name };
    });
  }, [
    formData.location,
    raisedByAccountId,
    reportingSupervisorsForRaisedBy,
    formData.managerOnDuty,
    formData.managerOnDutyName,
    isFetchingManagerOnDuty,
    editingComplaintId,
  ]);

  const keptExistingAttachments = useMemo(
    () =>
      formData.existingAttachments.filter(
        (a) => !(a.id && formData.removeAttachmentIds.map(String).includes(String(a.id)))
      ),
    [formData.existingAttachments, formData.removeAttachmentIds]
  );

  useEffect(() => {
    const files = formData.attachmentFiles || [];
    const urls = files.map((f) => URL.createObjectURL(f));
    setNewAttachmentPreviewUrls(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [formData.attachmentFiles]);

  const resetComplaintAttachmentInput = () => {
    if (complaintAttachmentInputRef.current) complaintAttachmentInputRef.current.value = '';
  };

  const handleAttachmentsInputChange = (e) => {
    const selected = Array.from(e.target.files || []);
    e.target.value = '';
    const slots =
      COMPLAINT_ATTACHMENT_MAX_FILES -
      keptExistingAttachments.length -
      (formData.attachmentFiles || []).length;
    if (slots <= 0) {
      toast.error(`You can upload at most ${COMPLAINT_ATTACHMENT_MAX_FILES} files.`);
      return;
    }
    const toAdd = selected.slice(0, slots);
    if (selected.length > toAdd.length) {
      toast.warn(
        `Only ${toAdd.length} more file(s) added (max ${COMPLAINT_ATTACHMENT_MAX_FILES} total).`
      );
    }
    const merged = [...(formData.attachmentFiles || []), ...toAdd];
    const err = validateComplaintMediaFiles(merged);
    if (err) {
      toast.error(err);
      return;
    }
    setFormData((prev) => ({ ...prev, attachmentFiles: merged }));
    setCtmFieldErrors((prev) => ({ ...prev, attachments: '' }));
  };

  const removeNewAttachmentAt = (index) => {
    setFormData((prev) => ({
      ...prev,
      attachmentFiles: prev.attachmentFiles.filter((_, i) => i !== index),
    }));
    setCtmFieldErrors((prev) => ({ ...prev, attachments: '' }));
  };

  const removeExistingAttachment = (a) => {
    if (a.id) {
      setFormData((prev) => ({
        ...prev,
        removeAttachmentIds: [
          ...new Set([...prev.removeAttachmentIds.map(String), String(a.id)]),
        ],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        existingAttachments: prev.existingAttachments.filter((x) => x.url !== a.url),
      }));
    }
    setCtmFieldErrors((prev) => ({ ...prev, attachments: '' }));
  };

  const renderComplaintAttachmentsField = () => {
    const videoMb = Math.round(COMPLAINT_VIDEO_MAX_BYTES / (1024 * 1024));
    const files = formData.attachmentFiles || [];
    const totalCount = keptExistingAttachments.length + files.length;
    return (
      <div className="md:col-span-3 flex flex-col text-sm text-gray-700">
        <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0">
          {reqLabel('Attachments (image / video)')}
          <span className="text-xs font-normal text-gray-500">
            (max {COMPLAINT_ATTACHMENT_MAX_FILES}; images max {IMAGE_MAX_KB}KB; videos max {videoMb}
            MB)
          </span>
        </div>
        <div
          className={`flex flex-wrap gap-2 rounded-lg border border-dashed p-3 ${
            ctmFieldErrors.attachments ? 'border-red-500 bg-red-50/30' : 'border-gray-300 bg-[#F7E5D5]'
          }`}
        >
          {keptExistingAttachments.map((a) => (
            <div
              key={`e-${a.url}-${a.id || 'x'}`}
              className="relative h-24 w-24 overflow-hidden rounded-md border border-gray-200 bg-black/5"
            >
              {isVideoUrl(a.url, a.contentType) ? (
                <AuthorizedMedia
                  as="video"
                  src={a.url}
                  className="h-full w-full object-cover"
                  muted
                  playsInline
                />
              ) : (
                <AuthorizedMedia src={a.url} alt="" className="h-full w-full object-cover" />
              )}
              <button
                type="button"
                className="absolute right-1 top-1 rounded bg-black/60 px-1.5 text-xs text-white hover:bg-black/80"
                onClick={() => removeExistingAttachment(a)}
              >
                ×
              </button>
            </div>
          ))}
          {files.map((file, index) => {
            const previewUrl = newAttachmentPreviewUrls[index];
            const isVid = String(file.type || '').startsWith('video/');
            return (
              <div
                key={`n-${file.name}-${index}`}
                className="relative h-24 w-24 overflow-hidden rounded-md border border-gray-200 bg-black/5"
              >
                {isVid && previewUrl ? (
                  <AuthorizedMedia
                    as="video"
                    src={previewUrl}
                    className="h-full w-full object-cover"
                    muted
                    playsInline
                  />
                ) : previewUrl ? (
                  <AuthorizedMedia src={previewUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span className="flex h-full items-center justify-center text-[10px] text-gray-600">
                    File
                  </span>
                )}
                <button
                  type="button"
                  className="absolute right-1 top-1 rounded bg-black/60 px-1.5 text-xs text-white hover:bg-black/80"
                  onClick={() => removeNewAttachmentAt(index)}
                >
                  ×
                </button>
              </div>
            );
          })}
          {totalCount < COMPLAINT_ATTACHMENT_MAX_FILES && (
            <label className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-gray-400 bg-white/80 text-center text-[10px] text-gray-600 hover:bg-white">
              Add
              <input
                ref={complaintAttachmentInputRef}
                type="file"
                name="attachments"
                accept="image/*,video/*"
                multiple
                className="hidden"
                onChange={handleAttachmentsInputChange}
              />
            </label>
          )}
        </div>
        {ctmFieldErrors.attachments ? (
          <span className={fieldErrorTextClass}>{ctmFieldErrors.attachments}</span>
        ) : null}
        <p className="mt-1 text-xs text-gray-500">
          {totalCount} / {COMPLAINT_ATTACHMENT_MAX_FILES} files
        </p>
      </div>
    );
  };

  const complaints = useMemo(() => {
    const rows = complaintsRes?.data;
    if (!Array.isArray(rows)) return [];

    const locations = locationsRes?.data || [];

    const locationName = (locationId) => {
      if (locationId == null || locationId === '') return '—';
      const loc = locations.find((l) => String(l.id) === String(locationId));
      return loc?.name || `${locationId}`;
    };

    const reportingSupervisorName = (row) => {
      const nameFromApi = row?.reporting_supervisor_name;
      if (nameFromApi != null && String(nameFromApi).trim() !== '') return String(nameFromApi).trim();
      const id = row?.reporting_supervisor_id ?? row?.account_id;
      if (id == null || id === '') return '—';
      return `ID ${id}`;
    };

    /** API may send a display name in raised_by_name; do not prefix with "ID " when it is not a numeric id. */
    const raisedByLabel = (row) => {
      const raw = row?.raised_by_name;
      if (raw == null || raw === '') return '—';
      const s = String(raw).trim();
      if (!/^\d+$/.test(s)) return s;
      return s;
    };

    const mapped = rows.map((row) => {
      const nature =
        (row.issue_description && String(row.issue_description).trim()) ||
        (row.complaint_sub_type && String(row.complaint_sub_type).trim()) ||
        (row.nature_of_complaint_id != null
          ? `Complaint type #${row.nature_of_complaint_id}`
          : '—');
      const locLabel = locationName(row.location_name);
      const statusLabel = formatComplaintStatusLabel(row.status);
      const reportedDate = row.created_at
        ? new Date(row.created_at).toLocaleString(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
          })
        : '—';

      return {
        id: `${row.id}`,
        numericId: row.id,
        acknowledgementNumber: row.acknowledgement_number,
        nature: nature,
        outlet: locLabel,
        location: locLabel,
        raisedBy: raisedByLabel(row),
        managerOnDuty: reportingSupervisorName(row),
        reportedDate,
        tat: complaintTatDisplayValue(row),
        status: statusLabel,
        imageUrl: attachmentUrlsFromRaw(row)[0] || '',
        attachmentUrls: attachmentUrlsFromRaw(row),
        viewAttachments: parseExistingAttachmentsFromRaw(row),
        complaintType: row.complaint_type || '',
        issueDescription: row.issue_description || '',
        raw: row,
      };
    });

    // NOTE: Per requirement, do not filter complaints for employees.
    // If backend returns many complaints, show them all.
    return mapped;
  }, [complaintsRes?.data, locationsRes?.data]);

  const detailPanelRaw = useMemo(() => {
    if (viewMode !== 'detail' || !selectedComplaint) return null;
    const id = selectedComplaint.numericId;
    const rows = complaintsRes?.data;
    if (Array.isArray(rows)) {
      const row = rows.find((r) => String(r?.id) === String(id));
      if (row) return row;
    }
    return selectedComplaint.raw ?? null;
  }, [viewMode, selectedComplaint, complaintsRes?.data]);

  const detailComplaintRating = useMemo(() => {
    return submittedDetailRating ?? complaintRatingFromRaw(detailPanelRaw);
  }, [submittedDetailRating, detailPanelRaw]);

  const canEmployeeSubmitReview = useMemo(() => {
    if (!isEmployeeArea || !detailPanelRaw) return false;
    if (!complaintStatusAllowsEmployeeRating(detailPanelRaw)) return false;
    const existing = complaintRatingFromRaw(detailPanelRaw);
    if (existing?.rating >= 1) return false;
    if (submittedDetailRating?.rating >= 1) return false;
    return true;
  }, [isEmployeeArea, detailPanelRaw, submittedDetailRating]);

  useEffect(() => {
    if (viewMode !== 'detail') return;
    const fromApi = complaintRatingFromRaw(detailPanelRaw);
    if (fromApi?.rating >= 1) setSubmittedDetailRating(null);
  }, [viewMode, detailPanelRaw]);

  const handleFilterChange = (field, value) => {
    if (field === 'search') setPage(1);
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleStatusFilterChange = (e) => {
    const next = e.target.value || 'all';
    setPage(1);
    const status = isValidComplaintStatus(next) ? next : 'all';
    setSearchParams((prev) => applyComplaintStatusToSearchParams(prev, status), { replace: true });
  };

  const pageTitle = isAdmin ? 'Complaints' : 'Complaints';

  const handleOpenForm = () => {
    setEditingComplaintId(null);
    setSelectedComplaint(null);
    setFormData(createEmptyComplaintForm());
    resetComplaintAttachmentInput();
    setViewMode('form');
    setRaisedBySelectOption(null);
    setCtmFieldErrors({});
    resetResolutionFields();
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedComplaint(null);
    setEditingComplaintId(null);
    setRaisedBySelectOption(null);
    setCtmFieldErrors({});
    setFormData(createEmptyComplaintForm());
    resetComplaintAttachmentInput();
    resetDetailReplyForm();
    resetResolutionFields();
    resetReviewForm();
    resetTrackingModal();
  };

  const getFirstErrorMessage = (errs, orderedKeys = []) => {
    if (!errs || typeof errs !== 'object') return '';
    for (const k of orderedKeys) {
      const msg = errs[k];
      if (msg) return msg;
    }
    const first = Object.values(errs).find(Boolean);
    return first ? String(first) : '';
  };

  const handleSelectComplaint = (complaint) => {
    setSelectedComplaint(complaint);
    setViewMode('detail');
    resetDetailReplyForm();
    setSubmittedDetailRating(null);
    setIsReviewModalOpen(false);
    setReviewRating(0);
    setReviewFeedback('');
    resetTrackingModal();
  };

  const handleOpenReviewModal = () => {
    setReviewRating(0);
    setReviewFeedback('');
    setIsReviewModalOpen(true);
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!selectedComplaint) return;
    if (reviewRating < 1 || reviewRating > 5) {
      toast.error('Please select a star rating (1–5).');
      return;
    }
    const complaintId = selectedComplaint.numericId ?? detailPanelRaw?.id;
    if (complaintId == null || String(complaintId).trim() === '') {
      toast.error('Missing complaint id.');
      return;
    }
    try {
      await createComplaintRating({
        complaint_id: complaintId,
        rating: reviewRating,
        feedback: String(reviewFeedback || '').trim(),
      }).unwrap();
      setSubmittedDetailRating({
        rating: reviewRating,
        feedback: String(reviewFeedback || '').trim(),
      });
      setIsReviewModalOpen(false);
      toast.success('Review submitted.');
    } catch (err) {
      const msg =
        err?.data?.message || err?.message || 'Could not submit review. Please try again.';
      toast.error(msg);
    }
  };

  const handleDetailComplaintReplySubmit = async (e) => {
    e.preventDefault();
    if (!selectedComplaint) return;
    const trimmed = String(detailReplyComment || '').trim();
    if (!trimmed) {
      toast.error('Please enter a comment.');
      return;
    }
    const accountId = user?.id;
    if (accountId == null || String(accountId).trim() === '') {
      toast.error('Missing account. Please log in again.');
      return;
    }
    const complaintId = selectedComplaint.numericId ?? selectedComplaint.raw?.id;
    const statusFromComplaint = complaintStatusForPayload(true, selectedComplaint.raw);
    const rawForDept = detailPanelRaw ?? selectedComplaint.raw;
    const accountableDepartmentId =
      rawForDept?.accountable_department_id ??
      rawForDept?.accountableDepartmentId ??
      rawForDept?.department_id ??
      '';

    try {
      await createComplaintComment({
        comment: trimmed,
        account_id: accountId,
        commented_by_type: commentedByTypeForComments,
        complaint_id: complaintId,
        status: statusFromComplaint,
        rca: detailReplyRca,
        pa: detailReplyPa,
        ca: detailReplyCa,
        accountable_department_id: accountableDepartmentId,
      }).unwrap();
      toast.success('Reply posted.');
      resetDetailReplyForm();
    } catch (err) {
      const msg =
        err?.data?.message || err?.message || 'Could not post reply. Please try again.';
      toast.error(msg);
    }
  };

  const handleEditComplaint = (complaint) => {
    const raw = complaint?.raw || complaint || {};
    if (!canAccountEditComplaint(raw, loggedInAccountId, canEdit)) return;

    setSelectedComplaint(complaint);
    setEditingComplaintId(raw?.id ?? raw?.complaint_id ?? complaint?.numericId ?? null);

    const tab = String(raw?.complaint_type || complaint?.complaintType || '')
      .trim()
      .toUpperCase();
    if (tab === 'ITSM' || tab === 'CTM') setActiveTab(tab);

    const locations = locationsRes?.data || [];
    const employees = employeesRes?.data || [];

    const byNameId = (list, name, key = 'name') => {
      const n = String(name || '').trim().toLowerCase();
      if (!n) return '';
      const match = (Array.isArray(list) ? list : []).find(
        (x) => String(x?.[key] || '').trim().toLowerCase() === n
      );
      return match?.id != null ? String(match.id) : '';
    };

    const locationId =
      raw?.location_id ??
      raw?.locationId ??
      byNameId(locations, raw?.location_name || complaint?.location);

    const managerId =
      raw?.reporting_supervisor_id ??
      raw?.account_id ??
      raw?.manager_on_duty_id ??
      raw?.managerOnDutyId ??
      '';

    let raisedById =
      raw?.account_id ??
      raw?.complaint_raised_by ??
      raw?.raised_by ??
      raw?.raisedBy ??
      '';
    const raisedByNameFromApi =
      raw?.raised_by_name ??
      raw?.raisedByName ??
      (typeof raw?.raised_by === 'string' && !/^\d+$/.test(String(raw.raised_by).trim())
        ? raw.raised_by
        : '');
    // Some APIs only send `raised_by_name` (sometimes numeric as a string). Use it as an id fallback.
    if ((raisedById == null || String(raisedById) === '') && raisedByNameFromApi != null) {
      const s = String(raisedByNameFromApi).trim();
      if (/^\d+$/.test(s)) raisedById = s;
    }
    const raisedByLabelFromList =
      complaint?.raisedBy && String(complaint.raisedBy).trim() !== '—'
        ? String(complaint.raisedBy).trim()
        : '';
    const raisedByName = String(raisedByNameFromApi || raisedByLabelFromList || '').trim();
    if (raisedById != null && String(raisedById) !== '') {
      const employeeMatch = employees.find((x) => String(x.id) === String(raisedById));
      const labelFromEmployee = employeeMatch?.name;
      const labelFromName =
        raisedByName && !/^\d+$/.test(raisedByName) ? raisedByName : '';
      setRaisedBySelectOption({
        value: String(raisedById),
        label: String(labelFromName || labelFromEmployee || raisedById),
      });
    } else {
      setRaisedBySelectOption(null);
    }
    const issueText = raw?.issue_description ?? raw?.issueDescription ?? '';
    const productIdRaw = raw?.product_id ?? raw?.productId ?? '';
    const productCategoryIdRaw =
      raw?.product_category_id ??
      raw?.productCategoryId ??
      '';
    const productSubcategoryIdRaw =
      raw?.product_subcategory_id ??
      raw?.productSubcategoryId ??
      '';

    setFormData((prev) => ({
      ...prev,
      location: locationId != null ? String(locationId) : '',
      managerOnDuty: managerId != null ? String(managerId) : '',
      managerOnDutyName: String(raw?.reporting_supervisor_name ?? '').trim(),
      raisedBy: raisedById != null ? String(raisedById) : '',
      // ITSM uses date_of_incident; CTM also sends date_of_incident (sometimes via reported_date).
      incidentDate: toDateInputValue(
        raw?.date_of_incident ?? raw?.incident_date ?? raw?.incidentDate ?? ''
      ),
      // "Reported Date" in UI should reflect when the complaint was created (created_at),
      // not the incident date / reported_date fields which can differ by workflow.
      reportedDate: toDateInputValue(raw?.created_at ?? raw?.createdAt ?? ''),
      category: String(raw?.category_id ?? raw?.category ?? ''),
      quantity: complaintQuantityToFormValue(raw?.quantity),
      uom: complaintUomToFormValue(raw?.uom) || COMPLAINT_QUANTITY_NA,
      productId: productIdRaw != null && String(productIdRaw) !== '' ? String(productIdRaw) : '',
      productName: String(
        raw?.complaint_product_name ?? raw?.product_name ?? raw?.productName ?? ''
      ),
      productCategoryId:
        productCategoryIdRaw != null && String(productCategoryIdRaw) !== ''
          ? String(productCategoryIdRaw)
          : '',
      productSubcategoryId:
        productSubcategoryIdRaw != null && String(productSubcategoryIdRaw) !== ''
          ? String(productSubcategoryIdRaw)
          : '',
      subCategory: String(raw?.subcategory_id ?? raw?.subCategory ?? ''),
      batchNo: String(raw?.batch_number ?? raw?.batch_no ?? raw?.batchNo ?? '')
        .toUpperCase()
        .replace(/[^A-Za-z0-9]/g, ''),
      vehicle_storage_temperature: String(
        raw?.vehicle_storage_temperature ??
          raw?.vehicleStorageTemperature ??
          raw?.vehicle_storage_temp ??
          ''
      )
        .toUpperCase()
        .replace(/[^A-Za-z0-9]/g, ''),
      mfgDate: toDateInputValue(raw?.mfg_date ?? raw?.mfgDate ?? ''),
      bestBefore: toDateInputValue(raw?.use_best_before ?? raw?.best_before ?? raw?.bestBefore ?? ''),
      department: String(raw?.accountable_department_id ?? raw?.department ?? ''),
      priority: String(raw?.work_priority_id ?? raw?.priority_id ?? raw?.priority ?? ''),
      issueType: String(raw?.issue_type_id ?? raw?.issueType ?? ''),
      rmType:
        raw?.rm_building_id != null && String(raw.rm_building_id) !== ''
          ? 'Building'
          : raw?.rm_equipment_id != null && String(raw.rm_equipment_id) !== ''
            ? 'Equipment'
            : '',
      equipment: String(raw?.rm_equipment_id ?? raw?.equipment ?? ''),
      building: String(raw?.rm_building_id ?? raw?.building ?? ''),
      attachmentFiles: [],
      existingAttachments: parseExistingAttachmentsFromRaw(raw),
      removeAttachmentIds: [],
      issueDescription: String(issueText),
      // CTM submit uses `remarks` as issue_description
      remarks: String(issueText).slice(0, COMPLAINT_REMARKS_MAX_LEN),
    }));

    resetComplaintAttachmentInput();
    setCtmFieldErrors({});
    {
      const { rca, pa, ca } = latestRcaPaCaFromRaw(raw);
      setResolutionStatus(complaintStatusSelectValue(raw));
      setResolutionRca(rca || '');
      setResolutionPa(pa || '');
      setResolutionCa(ca || '');
      setResolutionToDepartment(latestTransferredToDepartmentId(raw) || '');
    }
    setViewMode('form');
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    let nextVal = value;
    if (name === 'quantity') {
      nextVal = complaintQuantityToFormValue(value);
    } else if (name === 'batchNo') {
      nextVal = String(value).toUpperCase().replace(/[^A-Za-z0-9]/g, '');
    } else if (name === 'remarks' || name === 'issueDescription') {
      nextVal = String(value).slice(0, COMPLAINT_REMARKS_MAX_LEN);
    }

    const bumpCtm = (key) =>
      setCtmFieldErrors((prev) => ({ ...prev, [key]: '' }));

    /** Form `name` vs keys returned by `validateCtmComplaintForm` (only where they differ). */
    const ctmErrorKeyForFormField = (fieldName) =>
      fieldName === 'vehicle_storage_temperature' ? 'vehicleStorageTemperature' : fieldName;

    if (name === 'location') {
      bumpCtm('location');
      bumpCtm('managerOnDuty');
      bumpCtm('raisedBy');
      setRaisedBySelectOption(null);
      setFormData((prev) => ({
        ...prev,
        location: nextVal,
        raisedBy: '',
        managerOnDuty: '',
        managerOnDutyName: '',
      }));
      return;
    }
    if (name === 'raisedBy') {
      bumpCtm('raisedBy');
      bumpCtm('managerOnDuty');
      const row = findComplaintRaisedByRowByAccountId(complaintRaisedByRows, nextVal);
      const accountId = row ? complaintRaisedByAccountId(row) : String(nextVal ?? '');
      setFormData((prev) => ({
        ...prev,
        raisedBy: accountId,
        managerOnDuty: '',
        managerOnDutyName: '',
      }));
      return;
    }
    if (name === 'category') {
      setCtmFieldErrors((prev) => {
        const n = { ...prev };
        delete n.category;
        delete n.subCategory;
        delete n.issueType;
        return n;
      });
      setFormData((prev) => ({
        ...prev,
        category: nextVal,
        subCategory: '',
        issueType: '',
      }));
      return;
    }
    if (name === 'subCategory') {
      setCtmFieldErrors((prev) => {
        const n = { ...prev };
        delete n.subCategory;
        delete n.issueType;
        return n;
      });
      setFormData((prev) => ({
        ...prev,
        subCategory: nextVal,
        issueType: '',
      }));
      return;
    }
    if (name === 'issueType') {
      setCtmFieldErrors((prev) => {
        const n = { ...prev };
        delete n.issueType;
        delete n.department;
        return n;
      });
      setFormData((prev) => ({
        ...prev,
        issueType: nextVal,
      }));
      return;
    }
    if (name === 'productId') {
      setCtmFieldErrors((prev) => {
        const n = { ...prev };
        delete n.productId;
        delete n.productCategoryId;
        delete n.productSubcategoryId;
        return n;
      });
      const product = productList.find((row) => String(row.id) === String(nextVal));
      const nextCategoryId = product ? productCategoryIdFromRow(product) : '';
      const nextSubcategoryId = product ? productSubcategoryIdFromRow(product) : '';
      const nextProductName = product ? productNameFromRow(product) : '';
      setFormData((prev) => ({
        ...prev,
        productId: nextVal,
        productName: nextProductName,
        productCategoryId: nextCategoryId ? String(nextCategoryId) : '',
        productSubcategoryId: nextSubcategoryId ? String(nextSubcategoryId) : '',
      }));
      return;
    }
    if (name === 'department') {
      setCtmFieldErrors((prev) => {
        const n = { ...prev };
        delete n.department;
        delete n.issueType;
        delete n.category;
        delete n.subCategory;
        return n;
      });
      setFormData((prev) => ({
        ...prev,
        department: nextVal,
        category: '',
        subCategory: '',
      }));
      return;
    }
    if (name === 'rmType') {
      bumpCtm('rmType');
      setFormData((prev) => ({
        ...prev,
        rmType: nextVal,
        equipment: nextVal === 'Equipment' ? prev.equipment : '',
        building: nextVal === 'Building' ? prev.building : '',
      }));
      return;
    }

    const clearFieldErrorKeys = new Set([
      'location',
      'managerOnDuty',
      'raisedBy',
      'incidentDate',
      'reportedDate',
      'priority',
      'quantity',
      'uom',
      'batchNo',
      'mfgDate',
      'bestBefore',
      'vehicle_storage_temperature',
      'remarks',
      'equipment',
      'building',
      'issueDescription',
    ]);
    if (clearFieldErrorKeys.has(name)) bumpCtm(ctmErrorKeyForFormField(name));

    setFormData((prev) => ({ ...prev, [name]: nextVal }));
  };

  const handleComplaintTabChange = (tab) => {
    if (tab !== 'ITSM' && tab !== 'CTM') return;
    if (tab === activeTab) return;
    if (editingComplaintId) return;
    setActiveTab(tab);
    setCtmFieldErrors({});
    resetComplaintAttachmentInput();
    setRaisedBySelectOption(null);
    // ITSM / CTM must not share selections (location, raised by, dates, etc.).
    setFormData(createEmptyComplaintForm());
  };

  const showResolutionToDepartment = useMemo(() => {
    return String(resolutionStatus || '').trim() === 'transferred';
  }, [resolutionStatus]);

  const resolutionUpdatesLocked = useMemo(
    () => complaintEverResolvedOrArchived(selectedComplaint?.raw),
    [selectedComplaint?.raw]
  );
  const canEditResolutionDetails = Boolean(
    editingComplaintId &&
      canEdit &&
      !isComplaintRaisedByAccount(selectedComplaint?.raw, loggedInAccountId)
  );

  const resolutionToDepartmentOptions = useMemo(() => {
    const sourceDepartmentId = selectedComplaint?.raw
      ? transferSourceDepartmentId(selectedComplaint.raw)
      : '';
    const defaultToDepartment = selectedComplaint?.raw
      ? latestTransferredToDepartmentId(selectedComplaint.raw)
      : '';
    const seen = new Set();
    const out = [];
    for (const d of accountableDepartmentsListRes?.data || []) {
      const rawId = d?.id;
      if (rawId === '' || rawId === undefined || rawId === null) continue;
      const key = String(rawId);
      if (key === sourceDepartmentId || seen.has(key)) continue;
      seen.add(key);
      out.push({
        value: key,
        label: d?.name ?? `Department ${rawId}`,
      });
    }
    if (
      defaultToDepartment &&
      !out.some((o) => o.value === defaultToDepartment) &&
      defaultToDepartment !== sourceDepartmentId
    ) {
      const name = latestTransferredToDepartmentName(selectedComplaint?.raw);
      out.unshift({
        value: defaultToDepartment,
        label:
          name != null && String(name).trim() !== ''
            ? String(name).trim()
            : `Department ${defaultToDepartment}`,
      });
    }
    return out;
  }, [accountableDepartmentsListRes?.data, selectedComplaint?.raw]);

  const buildSuperAdminResolutionComplaintFields = () => {
    if (!canEditResolutionDetails || resolutionUpdatesLocked) return {};
    const fields = {};
    const rca = String(resolutionRca || '').trim();
    const pa = String(resolutionPa || '').trim();
    const ca = String(resolutionCa || '').trim();
    if (rca) fields.rca = rca;
    if (pa) fields.pa = pa;
    if (ca) fields.ca = ca;
    if (String(resolutionStatus || '').trim() === 'transferred') {
      const toDept = String(resolutionToDepartment || '').trim();
      if (toDept) fields.accountable_department_id = Number(toDept);
    }
    return fields;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      canEditResolutionDetails &&
      !resolutionUpdatesLocked &&
      String(resolutionStatus || '').trim() === 'transferred' &&
      !String(resolutionToDepartment || '').trim()
    ) {
      toast.error('Please select To Department.');
      return;
    }
    if (activeTab === 'ITSM') {
      const itsmErrs = validateItsmComplaintForm({
        location: formData.location,
        managerOnDuty: formData.managerOnDuty,
        raisedBy: formData.raisedBy,
        incidentDate: formData.incidentDate,
        category: formData.category,
        subCategory: formData.subCategory,
        issueType: formData.issueType,
        department: formData.department,
        priority: formData.priority,
        equipment: formData.equipment,
        building: formData.building,
        issueDescription: formData.issueDescription,
        keptExistingAttachments,
        newAttachmentFiles: formData.attachmentFiles,
      });

      if (Object.keys(itsmErrs).length) {
        setCtmFieldErrors(itsmErrs);
        toast.error(
          getFirstErrorMessage(itsmErrs, [
            'location',
            'managerOnDuty',
            'raisedBy',
            'incidentDate',
            'category',
            'subCategory',
            'department',
            'issueType',
            'priority',
            'attachments',
            'issueDescription',
          ]) || 'Please fix the highlighted errors.'
        );
        return;
      }
      setCtmFieldErrors({});

      const complaint = {
        location_id: formData.location ? Number(formData.location) : undefined,
        reporting_supervisor_id: formData.managerOnDuty
          ? Number(formData.managerOnDuty)
          : undefined,
        complaint_raised_by: raisedByAccountId ? Number(raisedByAccountId) : undefined,
        date_of_incident: formData.incidentDate || undefined,
        category_id: formData.category ? Number(formData.category) : undefined,
        subcategory_id: formData.subCategory ? Number(formData.subCategory) : undefined,
        accountable_department_id: formData.department ? Number(formData.department) : undefined,
        work_priority_id: formData.priority ? Number(formData.priority) : undefined,
        issue_type_id: formData.issueType ? Number(formData.issueType) : undefined,
        issue_description: formData.issueDescription || undefined,
        status:
          canEditResolutionDetails && String(resolutionStatus || '').trim()
            ? String(resolutionStatus).trim()
            : complaintStatusForPayload(Boolean(editingComplaintId), selectedComplaint?.raw),
        complaint_type: 'ITSM',
        attachments: formData.attachmentFiles || [],
        remove_attachment_ids: (formData.removeAttachmentIds || [])
          .map((id) => Number(id))
          .filter((n) => Number.isFinite(n)),
        ...buildSuperAdminResolutionComplaintFields(),
      };

      try {
        if (editingComplaintId) {
          await updateComplaint({ id: editingComplaintId, complaint }).unwrap();
        } else {
          await createComplaint(complaint).unwrap();
        }
        toast.success(
          editingComplaintId
            ? 'Complaint updated successfully.'
            : 'Complaint submitted successfully.'
        );
        setViewMode('list');
        setEditingComplaintId(null);
        setSelectedComplaint(null);
        resetComplaintAttachmentInput();
        resetResolutionFields();
        setFormData(createEmptyComplaintForm());
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Create ITSM complaint failed:', err);
        toast.error(err?.data?.message || err?.message || 'Failed to save complaint.');
      }
      return;
    }

    if (activeTab === 'CTM') {
      const errs = validateCtmComplaintForm({
        location: formData.location,
        managerOnDuty: formData.managerOnDuty,
        raisedBy: formData.raisedBy,
        incidentDate: formData.incidentDate,
        reportedDate: formData.reportedDate,
        priority: formData.priority,
        vehicleStorageTemperature: formData.vehicle_storage_temperature,
        issueType: formData.issueType,
        department: formData.department,
        category: formData.category,
        subCategory: formData.subCategory,
        productId: formData.productId,
        productCategoryId: formData.productCategoryId,
        productSubcategoryId: formData.productSubcategoryId,
        remarks: formData.remarks,
        quantity: formData.quantity,
        uom: formData.uom,
        batchNo: formData.batchNo,
        mfgDate: formData.mfgDate,
        bestBefore: formData.bestBefore,
        keptExistingAttachments,
        newAttachmentFiles: formData.attachmentFiles,
      });

      if (Object.keys(errs).length) {
        setCtmFieldErrors(errs);
        toast.error(
          getFirstErrorMessage(errs, [
            'location',
            'managerOnDuty',
            'raisedBy',
            'issueType',
            'department',
            'priority',
            'vehicleStorageTemperature',
            'category',
            'subCategory',
            'productId',
            'productCategoryId',
            'productSubcategoryId',
            'attachments',
            'remarks',
            'quantity',
            'uom',
            'batchNo',
            'mfgDate',
            'bestBefore',
            'incidentDate',
            'reportedDate',
          ]) || 'Please fix the highlighted errors.'
        );
        return;
      }
      setCtmFieldErrors({});

      const complaint = {
        location_id: formData.location ? Number(formData.location) : undefined,
        reporting_supervisor_id: formData.managerOnDuty
          ? Number(formData.managerOnDuty)
          : undefined,
        complaint_raised_by: raisedByAccountId ? Number(raisedByAccountId) : undefined,
        date_of_incident: formData.incidentDate || formData.reportedDate || undefined,
        category_id: formData.category ? Number(formData.category) : undefined,
        subcategory_id: formData.subCategory ? Number(formData.subCategory) : undefined,
        complaint_sub_type:
          (issueTypesRes?.data || []).find((item) => String(item.id) === String(formData.issueType))
            ?.name || undefined,
        quantity: complaintQuantityToPayload(formData.quantity),
        uom:
          String(formData.uom).replace(/\s/g, '').toUpperCase() === 'N/A'
            ? COMPLAINT_QUANTITY_NA
            : formData.uom || undefined,
        batch_number: formData.batchNo || undefined,
        vehicle_storage_temperature: formData.vehicle_storage_temperature || undefined,
        mfg_date: formData.mfgDate || undefined,
        use_best_before: formData.bestBefore || undefined,
        status:
          canEditResolutionDetails && String(resolutionStatus || '').trim()
            ? String(resolutionStatus).trim()
            : complaintStatusForPayload(Boolean(editingComplaintId), selectedComplaint?.raw),
        complaint_type: 'CTM',
        accountable_department_id: formData.department ? Number(formData.department) : undefined,
        work_priority_id: formData.priority ? Number(formData.priority) : undefined,
        issue_type_id: formData.issueType ? Number(formData.issueType) : undefined,
        product_id: formData.productId ? Number(formData.productId) : undefined,
        product_name: formData.productName?.trim() || undefined,
        product_category_id: formData.productCategoryId
          ? Number(formData.productCategoryId)
          : undefined,
        product_subcategory_id: formData.productSubcategoryId
          ? Number(formData.productSubcategoryId)
          : undefined,
        issue_description: formData.remarks || undefined,
        attachments: formData.attachmentFiles || [],
        remove_attachment_ids: (formData.removeAttachmentIds || [])
          .map((id) => Number(id))
          .filter((n) => Number.isFinite(n)),
        ...buildSuperAdminResolutionComplaintFields(),
      };

      try {
        if (editingComplaintId) {
          await updateComplaint({ id: editingComplaintId, complaint }).unwrap();
        } else {
          await createComplaint(complaint).unwrap();
        }
        toast.success(
          editingComplaintId
            ? 'Complaint updated successfully.'
            : 'Complaint submitted successfully.'
        );
        setViewMode('list');
        setEditingComplaintId(null);
        setSelectedComplaint(null);
        resetComplaintAttachmentInput();
        resetResolutionFields();
        setFormData(createEmptyComplaintForm());
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Create complaint failed:', err);
        toast.error(err?.data?.message || err?.message || 'Failed to save complaint.');
      }
      return;
    }
  };

  const pageSize = DEFAULT_PAGE_SIZE;
  const { currentPage, totalCount, pageSize: effectivePageSize } = getServerPagination(
    complaintsRes,
    { page, pageSize }
  );
  const pageItems = complaints;

  const isComplaintDetailItsm =
    viewMode === 'detail' &&
    selectedComplaint &&
    String(selectedComplaint.complaintType || '').toUpperCase() === 'ITSM';

  const layoutTitle = viewMode === 'form' ? (editingComplaintId ? 'Edit Complaint' : 'Add Complaint') : pageTitle;
  return (
    <DashboardLayout title={layoutTitle}>
      <div className="complaints-page space-y-4">
        {viewMode === 'list' && (
          <div className="space-y-3">
            <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
              <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex w-full flex-col gap-3 md:max-w-2xl md:flex-row md:items-center">
                  <div className="relative w-full min-w-0 md:max-w-xs">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      placeholder="Search Complaints"
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="w-full rounded-lg border border-gray-200 bg-[#faf8f5] py-2 pl-10 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                    />
                  </div>
                  <label className="flex w-full min-w-0 flex-col gap-1.5 text-sm text-gray-700 md:flex-row md:items-center md:gap-2 md:w-56 md:shrink-0">
                    <span className="shrink-0 font-medium">Filter By:</span>
                    <div className="w-full min-w-0">
                      <SearchableSelect
                        name="complaintStatus"
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                        options={COMPLAINT_LIST_STATUS_OPTIONS.map(({ label, value }) => ({
                          label,
                          value,
                        }))}
                        placeholder="All Complaints"
                        clearable={false}
                        className={complaintStatusSelectClasses}
                      />
                    </div>
                  </label>
                </div>
                {canCreate ? (
                  <button
                    type="button"
                    className="inline-flex w-full shrink-0 items-center justify-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm md:w-auto"
                    onClick={handleOpenForm}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Add Complaint
                  </button>
                ) : null}
              </div>

              <ApiLoadState
                isLoading={isComplaintsLoading}
                isFetching={isComplaintsFetching}
                hasData={complaints.length > 0}
                error={complaintsError}
                refetch={refetchComplaints}
                message="Loading complaints…"
                className="py-6 md:hidden"
              />
              {!complaintsError &&
              (complaints.length > 0 || (!isComplaintsLoading && !isComplaintsFetching)) &&
              pageItems.length === 0 ? (
                <p className="py-6 text-center text-sm text-gray-500 md:hidden">No complaints found.</p>
              ) : !complaintsError &&
                (complaints.length > 0 || (!isComplaintsLoading && !isComplaintsFetching)) &&
                pageItems.length > 0 ? (
                <ul className="mb-4 space-y-3 md:hidden">
                  {pageItems.map((row, index) => (
                    <li
                      key={row.numericId ?? row.id}
                      className="rounded-lg border border-gray-100 bg-[#faf8f5] p-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-gray-500">
                            #{(currentPage - 1) * pageSize + index + 1} · {row.complaintType}
                          </p>
                          <p className="mt-1 truncate text-sm font-semibold text-gray-900">
                            {row.location}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          {canView ? (
                            <button
                              type="button"
                              onClick={() => handleSelectComplaint(row)}
                              className="rounded-lg p-1.5 text-red-700 hover:bg-white"
                              aria-label="View complaint"
                            >
                              <FaEye className="h-5 w-5" />
                            </button>
                          ) : null}
                          {canEdit ? (
                            <button
                              type="button"
                              onClick={() => handleEditComplaint(row)}
                              disabled={!canAccountEditComplaint(row.raw, loggedInAccountId, canEdit)}
                              className="rounded-lg p-1.5 text-red-700 hover:bg-white disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                              aria-label="Edit complaint"
                              title={
                                canAccountEditComplaint(row.raw, loggedInAccountId, canEdit)
                                  ? 'Edit complaint'
                                  : 'Edit is allowed only while the complaint is pending'
                              }
                            >
                              <MdEdit className="h-5 w-5" />
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <div className="mt-2 space-y-1 text-xs text-gray-600">
                        <p>
                          Raised By:{' '}
                          <span className="font-medium text-gray-800">{row.raisedBy}</span>
                        </p>
                        <p>
                          Manager:{' '}
                          <span className="font-medium text-gray-800">{row.managerOnDuty}</span>
                        </p>
                        <p className="text-[11px] text-gray-500">{row.reportedDate}</p>
                        <p>
                          TAT:{' '}
                          <span className="font-medium text-gray-800">{row.tat}</span>
                        </p>
                      </div>
                      <div className="mt-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${getStatusClasses(
                            row.status,
                          )}`}
                        >
                          {row.status}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : null}

              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="">
                      <th className="whitespace-nowrap border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                        Sr. No.
                      </th>
                      <th className="whitespace-nowrap border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                        Complaint ID
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                        Complaint Type
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                        Location
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                        Raised By
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                        Reporting Supervisor
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                        Reported Date
                      </th>
                      <th className="whitespace-nowrap border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                        TAT
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                        Status
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <ApiLoadTableRow
                      colSpan={10}
                      isLoading={isComplaintsLoading}
                      isFetching={isComplaintsFetching}
                      hasData={complaints.length > 0}
                      error={complaintsError}
                      refetch={refetchComplaints}
                      message="Loading complaints…"
                    />
                    {!complaintsError &&
                      (complaints.length > 0 ||
                        (!isComplaintsLoading && !isComplaintsFetching)) &&
                      pageItems.map((row, index) => (
                      <tr key={row.numericId ?? row.id} className="hover:bg-amber-50/40">
                        <td className="border-b border-gray-100 px-3 py-2.5 whitespace-nowrap text-gray-700">
                        {(currentPage - 1) * pageSize + index + 1}
                        </td>
                        <td className="border-b border-gray-100 px-3 py-2.5 whitespace-nowrap text-gray-700">
                          {row.acknowledgementNumber}
                        </td>
                        <td className="border-b border-gray-100 px-3 py-2.5 whitespace-nowrap text-gray-700">
                          {row.complaintType}
                        </td>
                        <td className="border-b border-gray-100 px-3 py-2.5 whitespace-nowrap text-gray-700">
                          {row.location}
                        </td>
                        <td className="border-b border-gray-100 px-3 py-2.5 whitespace-nowrap text-gray-700">
                          {row.raisedBy}
                        </td>
                        <td className="border-b border-gray-100 px-3 py-2.5 whitespace-nowrap text-gray-700">
                          {row.managerOnDuty}
                        </td>
                        <td className="border-b border-gray-100 px-3 py-2.5 whitespace-nowrap text-gray-700">
                          {row.reportedDate}
                        </td>
                        <td className="border-b border-gray-100 px-3 py-2.5 whitespace-nowrap text-gray-700">
                          {row.tat}
                        </td>
                        <td className="border-b border-gray-100 px-3 py-2.5">
                          <span
                            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${getStatusClasses(
                              row.status
                            )}`}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td className="border-b border-gray-100 px-3 py-2.5">
                          {canView ? (
                            <button
                              type="button"
                              onClick={() => handleSelectComplaint(row)}
                              className="text-red-700 hover:text-red-800"
                              aria-label="View complaint"
                            >
                              <FaEye className="h-5 w-5 text-red-700" />
                            </button>
                          ) : null}

                          {canEdit ? (
                            <button
                              type="button"
                              onClick={() => handleEditComplaint(row)}
                              disabled={!canAccountEditComplaint(row.raw, loggedInAccountId, canEdit)}
                              className="ml-3 text-red-700 hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-red-700"
                              aria-label="Edit complaint"
                              title={
                                canAccountEditComplaint(row.raw, loggedInAccountId, canEdit)
                                  ? 'Edit complaint'
                                  : 'Edit is allowed only while the complaint is pending'
                              }
                            >
                              <MdEdit className="h-5 w-5 text-red-700" />
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                    {!complaintsError &&
                      !isComplaintsLoading &&
                      !isComplaintsFetching &&
                      pageItems.length === 0 && (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-3 py-8 text-center text-sm text-gray-500"
                        >
                          No complaints found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <TablePagination
                totalCount={totalCount}
                currentPage={currentPage}
                onPageChange={setPage}
                pageSize={effectivePageSize}
              />
            </div>
          </div>
        )}

        {viewMode === 'form' && (
          <div className="space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-lg font-bold text-brand underline decoration-brand/30 underline-offset-4">

              </h2>
              <div className="flex items-center gap-4">
                <p className="text-sm text-gray-500">
                  <Link
                    to={isAdmin ? '/dashboard/admin' : '/dashboard/employee'}
                    className="text-brand hover:underline"
                  >
                    Home
                  </Link>
                  {' / '}
                  <button
                    type="button"
                    onClick={handleBackToList}
                    className="text-brand hover:underline"
                  >
                    Complaints
                  </button>
                  {' / '}
                  <span className="text-gray-400">
                    {editingComplaintId ? 'Edit Complaint' : 'Add Complaint'}
                  </span>
                </p>
              </div>
            </div>

            <div className="rounded-2xl bg-white shadow-sm">
              <div className="flex items-center gap-2 border-b border-gray-200 px-6 pt-4">
                <button
                  type="button"
                  disabled={Boolean(editingComplaintId) && activeTab !== 'ITSM'}
                  className={`rounded-t-md px-5 py-2 text-sm font-semibold ${
                    activeTab === 'ITSM'
                      ? 'bg-brand text-white'
                      : 'bg-gray-100 text-gray-400'
                  } ${editingComplaintId && activeTab !== 'ITSM' ? 'cursor-not-allowed opacity-60' : ''}`}
                  onClick={() => handleComplaintTabChange('ITSM')}
                >
                  ITSM
                </button>
                <button
                  type="button"
                  disabled={Boolean(editingComplaintId) && activeTab !== 'CTM'}
                  className={`rounded-t-md px-5 py-2 text-sm font-semibold ${
                    activeTab === 'CTM'
                      ? 'bg-brand text-white'
                      : 'bg-gray-100 text-gray-400'
                  } ${editingComplaintId && activeTab !== 'CTM' ? 'cursor-not-allowed opacity-60' : ''}`}
                  onClick={() => handleComplaintTabChange('CTM')}
                >
                  CTM
                </button>
              </div>

              <div className="p-6">
                <h3 className="mb-4 text-base font-semibold text-gray-800">
                  {editingComplaintId ? 'Edit Complaint' : 'Add Complaint'}
                </h3>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-3">
                    {activeTab === 'ITSM' && (
                      <>
                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Location')}
                          <SearchableSelect
                            name="location"
                            value={formData.location}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${ctmFieldErrors.location ? ` ${inputErrorClasses}` : ''}`}
                            options={locationSelectOptions}
                          />
                          {ctmFieldErrors.location ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.location}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Complaint Raised By')}
                          <SearchableSelect
                            name="raisedBy"
                            value={formData.raisedBy}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${ctmFieldErrors.raisedBy ? ` ${inputErrorClasses}` : ''}`}
                            disabled={!formData.location}
                            options={raisedByOptions}
                          />
                          {ctmFieldErrors.raisedBy ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.raisedBy}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                           Reporting Supervisor
                          <input
                            type="text"
                            readOnly
                            disabled
                            value={managerOnDutyDisplayName}
                            placeholder={
                              !formData.location
                                ? 'Select Location first'
                                : !formData.raisedBy
                                  ? 'Select Complaint Raised By first'
                                  : '—'
                            }
                            className={`${fieldInputClasses} cursor-not-allowed opacity-90${
                              ctmFieldErrors.managerOnDuty ? ` ${inputErrorClasses}` : ''
                            }`}
                          />
                          {ctmFieldErrors.managerOnDuty ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.managerOnDuty}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Date of Incident')}
                          <input
                            type="date"
                            name="incidentDate"
                            value={formData.incidentDate || ''}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${
                              ctmFieldErrors.incidentDate ? ` ${inputErrorClasses}` : ''
                            }`}
                          />
                          {ctmFieldErrors.incidentDate ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.incidentDate}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Work Priority')}
                          <SearchableSelect
                            name="priority"
                            value={formData.priority}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${ctmFieldErrors.priority ? ` ${inputErrorClasses}` : ''}`}
                            options={(workPrioritiesRes?.data || []).map((priority) => ({
                              value: String(priority.id),
                              label: priority.name ?? String(priority.id),
                            }))}
                          />
                          {ctmFieldErrors.priority ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.priority}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Accountable Department')}
                          <SearchableSelect
                            name="department"
                            value={formData.department}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${ctmFieldErrors.department ? ` ${inputErrorClasses}` : ''}`}
                            disabled={itsmDepartmentSelectOptions.length === 0}
                            options={itsmDepartmentSelectOptions.map((department) => ({
                              value: String(department.id),
                              label: department.name ?? String(department.id),
                            }))}
                          />
                          {ctmFieldErrors.department ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.department}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Complaint Category')}
                          <SearchableSelect
                            name="category"
                            value={formData.category}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${
                              ctmFieldErrors.category ? ` ${inputErrorClasses}` : ''
                            }`}
                            disabled={!formData.department}
                            options={(categoriesRes?.data || []).map((category) => ({
                              value: String(category.id),
                              label: category.name ?? String(category.id),
                            }))}
                          />
                          {ctmFieldErrors.category ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.category}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Complaint Sub Category')}
                          <SearchableSelect
                            name="subCategory"
                            value={formData.subCategory}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${
                              ctmFieldErrors.subCategory ? ` ${inputErrorClasses}` : ''
                            }`}
                            disabled={!formData.category}
                            options={(subcategoriesRes?.data || []).map((subcategory) => ({
                              value: String(subcategory.id),
                              label: subcategory.name ?? String(subcategory.id),
                            }))}
                          />
                          {ctmFieldErrors.subCategory ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.subCategory}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Issue Type')}
                          <SearchableSelect
                            name="issueType"
                            value={formData.issueType || ''}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${ctmFieldErrors.issueType ? ` ${inputErrorClasses}` : ''}`}
                            disabled={!formData.subCategory}
                            options={issueTypeOptionsBySubcategory.map((issueType) => ({
                              value: String(issueType.id),
                              label: issueType.name ?? String(issueType.id),
                            }))}
                          />
                          {ctmFieldErrors.issueType ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.issueType}</span>
                          ) : null}
                        </label>
                        

                        {renderComplaintAttachmentsField()}

                        <label className="md:col-span-2 flex flex-col text-sm text-gray-700">
                          {reqLabel('Issue Description')}
                          <textarea
                            name="issueDescription"
                            placeholder="Describe Product Issue (max 100 characters)"
                            rows={4}
                            maxLength={COMPLAINT_REMARKS_MAX_LEN}
                            value={formData.issueDescription || ''}
                            onChange={handleFormChange}
                            className={`${fieldTextareaClasses}${
                              ctmFieldErrors.issueDescription ? ` ${inputErrorClasses}` : ''
                            }`}
                          />
                          <span className="mt-1 text-xs text-gray-500">
                            {String(formData.issueDescription || '').length}/{COMPLAINT_REMARKS_MAX_LEN}
                          </span>
                          {ctmFieldErrors.issueDescription ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.issueDescription}</span>
                          ) : null}
                        </label>
                      </>
                    )}

                    {activeTab === 'CTM' && (
                      <>
                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Location')}
                          <SearchableSelect
                            name="location"
                            value={formData.location}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${ctmFieldErrors.location ? ` ${inputErrorClasses}` : ''}`}
                            options={locationSelectOptions}
                          />
                          {ctmFieldErrors.location ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.location}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Complaint Raised By')}
                          <SearchableSelect
                            name="raisedBy"
                            value={formData.raisedBy}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${ctmFieldErrors.raisedBy ? ` ${inputErrorClasses}` : ''}`}
                            disabled={!formData.location}
                            options={raisedByOptions}
                          />
                          {ctmFieldErrors.raisedBy ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.raisedBy}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                            Reporting Supervisor
                          <input
                            type="text"
                            readOnly
                            disabled
                            value={managerOnDutyDisplayName}
                            placeholder={
                              !formData.location
                                ? 'Select Location first'
                                : !formData.raisedBy
                                  ? 'Select Complaint Raised By first'
                                  : '—'
                            }
                            className={`${fieldInputClasses} cursor-not-allowed opacity-90${
                              ctmFieldErrors.managerOnDuty ? ` ${inputErrorClasses}` : ''
                            }`}
                          />
                          {ctmFieldErrors.managerOnDuty ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.managerOnDuty}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Date of Incident')}
                          <input
                            type="date"
                            name="incidentDate"
                            value={formData.incidentDate || ''}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${
                              ctmFieldErrors.incidentDate ? ` ${inputErrorClasses}` : ''
                            }`}
                          />
                          {ctmFieldErrors.incidentDate ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.incidentDate}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Reported Date')}
                          <input
                            type="date"
                            name="reportedDate"
                            value={formData.reportedDate}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${
                              ctmFieldErrors.reportedDate ? ` ${inputErrorClasses}` : ''
                            }`}
                          />
                          {ctmFieldErrors.reportedDate ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.reportedDate}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Quantity')}
                          <SearchableSelect
                            name="quantity"
                            value={formData.quantity}
                            onChange={handleFormChange}
                            placeholder="Select quantity"
                            className={`${fieldInputClasses}${
                              ctmFieldErrors.quantity ? ` ${inputErrorClasses}` : ''
                            }`}
                            options={[
                              { value: 'N/A', label: 'N/A' },
                              ...Array.from({ length: 100 }, (_, idx) => {
                                const v = String(idx + 1);
                                return { value: v, label: v };
                              }),
                            ]}
                          />
                          {ctmFieldErrors.quantity ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.quantity}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('UOM')}
                          <SearchableSelect
                            name="uom"
                            value={formData.uom}
                            onChange={handleFormChange}
                            placeholder="Select UOM"
                            className={`${fieldInputClasses}${
                              ctmFieldErrors.uom ? ` ${inputErrorClasses}` : ''
                            }`}
                            options={[
                              { value: 'N/A', label: 'N/A' },
                              { value: 'Kg', label: 'Kg' },
                              { value: 'Gm', label: 'Gm' },
                              { value: 'Litre', label: 'Litre' },
                              { value: 'Number', label: 'Number' },
                              
                            ]}
                          />
                          {ctmFieldErrors.uom ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.uom}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Work Priority')}
                          <SearchableSelect
                            name="priority"
                            value={formData.priority}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${ctmFieldErrors.priority ? ` ${inputErrorClasses}` : ''}`}
                            options={(workPrioritiesRes?.data || []).map((priority) => ({
                              value: String(priority.id),
                              label: priority.name ?? String(priority.id),
                            }))}
                          />
                          {ctmFieldErrors.priority ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.priority}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Accountable Department')}
                          <SearchableSelect
                            name="department"
                            value={formData.department}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${ctmFieldErrors.department ? ` ${inputErrorClasses}` : ''}`}
                            disabled={itsmDepartmentSelectOptions.length === 0}
                            options={itsmDepartmentSelectOptions.map((department) => ({
                              value: String(department.id),
                              label: department.name ?? String(department.id),
                            }))}
                          />
                          {ctmFieldErrors.department ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.department}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Complaint Category')}
                          <SearchableSelect
                            name="category"
                            value={formData.category}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${ctmFieldErrors.category ? ` ${inputErrorClasses}` : ''}`}
                            disabled={!formData.department}
                            options={(categoriesRes?.data || []).map((category) => ({
                              value: String(category.id),
                              label: category.name ?? String(category.id),
                            }))}
                          />
                          {ctmFieldErrors.category ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.category}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Complaint Sub Category')}
                          <SearchableSelect
                            name="subCategory"
                            value={formData.subCategory}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${ctmFieldErrors.subCategory ? ` ${inputErrorClasses}` : ''}`}
                            disabled={!formData.category}
                            options={(subcategoriesRes?.data || []).map((subcategory) => ({
                              value: String(subcategory.id),
                              label: subcategory.name ?? String(subcategory.id),
                            }))}
                          />
                          {ctmFieldErrors.subCategory ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.subCategory}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Issue Type')}
                          <SearchableSelect
                            name="issueType"
                            value={formData.issueType || ''}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${ctmFieldErrors.issueType ? ` ${inputErrorClasses}` : ''}`}
                            disabled={!formData.subCategory}
                            options={issueTypeOptionsBySubcategory.map((issueType) => ({
                              value: String(issueType.id),
                              label: issueType.name ?? String(issueType.id),
                            }))}
                          />
                          {ctmFieldErrors.issueType ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.issueType}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Product Name')}
                          <SearchableSelect
                            name="productId"
                            value={formData.productId}
                            onChange={handleFormChange}
                            placeholder="Select product"
                            className={`${fieldInputClasses}${
                              ctmFieldErrors.productId ? ` ${inputErrorClasses}` : ''
                            }`}
                            options={productSelectOptions}
                          />
                          {ctmFieldErrors.productId ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.productId}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Product Category')}
                          <SearchableSelect
                            name="productCategoryId"
                            value={formData.productCategoryId}
                            onChange={handleFormChange}
                            placeholder="Select product first"
                            disabled
                            className={`${fieldInputClasses} cursor-not-allowed opacity-90${
                              ctmFieldErrors.productCategoryId ? ` ${inputErrorClasses}` : ''
                            }`}
                            options={productCategorySelectOptions}
                          />
                          {ctmFieldErrors.productCategoryId ? (
                            <span className={fieldErrorTextClass}>
                              {ctmFieldErrors.productCategoryId}
                            </span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Product Sub Category')}
                          <SearchableSelect
                            name="productSubcategoryId"
                            value={formData.productSubcategoryId}
                            onChange={handleFormChange}
                            placeholder="Select product first"
                            disabled
                            className={`${fieldInputClasses} cursor-not-allowed opacity-90${
                              ctmFieldErrors.productSubcategoryId ? ` ${inputErrorClasses}` : ''
                            }`}
                            options={productSubcategorySelectOptions}
                          />
                          {ctmFieldErrors.productSubcategoryId ? (
                            <span className={fieldErrorTextClass}>
                              {ctmFieldErrors.productSubcategoryId}
                            </span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Batch No. (As Per Actual)')}
                          <input
                            type="text"
                            name="batchNo"
                            autoComplete="off"
                            value={formData.batchNo || ''}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${
                              ctmFieldErrors.batchNo ? ` ${inputErrorClasses}` : ''
                            }`}
                            placeholder="Enter Batch No."
                          />
                          {ctmFieldErrors.batchNo ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.batchNo}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('MFG Date')}
                          <input
                            type="date"
                            name="mfgDate"
                            value={formData.mfgDate || ''}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${
                              ctmFieldErrors.mfgDate ? ` ${inputErrorClasses}` : ''
                            }`}
                          />
                          {ctmFieldErrors.mfgDate ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.mfgDate}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Use Before / Best Before')}
                          <input
                            type="date"
                            name="bestBefore"
                            value={formData.bestBefore || ''}
                            onChange={handleFormChange}
                            className={`${fieldInputClasses}${
                              ctmFieldErrors.bestBefore ? ` ${inputErrorClasses}` : ''
                            }`}
                          />
                          {ctmFieldErrors.bestBefore ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.bestBefore}</span>
                          ) : null}
                        </label>

                        <label className="flex flex-col text-sm text-gray-700">
                          {reqLabel('Vehicle Storage Temperature')}
                          <input
                            type="text"
                            name="vehicle_storage_temperature"
                            autoComplete="off"
                            value={formData.vehicle_storage_temperature || ''}
                            onChange={handleFormChange}
                            placeholder="Enter Vehicle Storage Temperature"
                            className={`${fieldInputClasses}${
                              ctmFieldErrors.vehicleStorageTemperature
                                ? ` ${inputErrorClasses}`
                                : ''
                            }`}
                          />
                          {ctmFieldErrors.vehicleStorageTemperature ? (
                            <span className={fieldErrorTextClass}>
                              {ctmFieldErrors.vehicleStorageTemperature}
                            </span>
                          ) : null}
                        </label>
                        {renderComplaintAttachmentsField()}

                        <label className="md:col-span-2 flex flex-col text-sm text-gray-700">
                          {reqLabel('Remarks')}
                          <textarea
                            name="remarks"
                            placeholder="Describe Product Issue (max 100 characters)"
                            rows={4}
                            maxLength={COMPLAINT_REMARKS_MAX_LEN}
                            value={formData.remarks}
                            onChange={handleFormChange}
                            className={`${fieldTextareaClasses}${
                              ctmFieldErrors.remarks ? ` ${inputErrorClasses}` : ''
                            }`}
                          />
                          <span className="mt-1 text-xs text-gray-500">
                            {String(formData.remarks || '').length}/{COMPLAINT_REMARKS_MAX_LEN}
                          </span>
                          {ctmFieldErrors.remarks ? (
                            <span className={fieldErrorTextClass}>{ctmFieldErrors.remarks}</span>
                          ) : null}
                        </label>
                      </>
                    )}
                  </div>

                  {canEditResolutionDetails ? (
                    <div className="mt-6 space-y-4">
                      <div>
                        <h4 className="text-base font-semibold text-gray-900">Resolution details</h4>

                      </div>
                      {resolutionUpdatesLocked ? (
                        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
                          This complaint is resolved or archived and resolution fields can no longer
                          be updated.
                        </p>
                      ) : null}
                      <div
                        className={`grid grid-cols-1 gap-4 ${
                          showResolutionToDepartment ? 'sm:grid-cols-2' : 'sm:grid-cols-1'
                        }`}
                      >
                        <label className="flex flex-col text-sm text-gray-700">
                          Status
                          <SearchableSelect
                            name="resolutionStatus"
                            value={resolutionStatus}
                            onChange={(e) => {
                              const next = e.target.value;
                              setResolutionStatus(next);
                              if (next !== 'transferred') {
                                setResolutionToDepartment('');
                              } else if (
                                !String(resolutionToDepartment || '').trim() &&
                                selectedComplaint?.raw
                              ) {
                                setResolutionToDepartment(
                                  latestTransferredToDepartmentId(selectedComplaint.raw) || ''
                                );
                              }
                            }}
                            options={COMPLAINT_STATUS_SELECT_OPTIONS}
                            placeholder="Select status"
                            className={`${fieldInputClasses}${
                              resolutionUpdatesLocked ? ` ${resolutionFieldDisabledClasses}` : ''
                            }`}
                            disabled={resolutionUpdatesLocked}
                          />
                        </label>
                        {showResolutionToDepartment ? (
                          <label className="flex flex-col text-sm text-gray-700">
                            <span>
                              To Department <span className="text-red-600">*</span>
                            </span>
                            <SearchableSelect
                              name="resolutionToDepartment"
                              value={resolutionToDepartment}
                              onChange={(e) => setResolutionToDepartment(e.target.value)}
                              options={resolutionToDepartmentOptions}
                              placeholder="Select department"
                              className={`${fieldInputClasses}${
                                resolutionUpdatesLocked ? ` ${resolutionFieldDisabledClasses}` : ''
                              }`}
                              disabled={
                                resolutionUpdatesLocked ||
                                resolutionToDepartmentOptions.length === 0
                              }
                              clearable={false}
                            />
                          </label>
                        ) : null}
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <label className="flex flex-col text-sm text-gray-700">
                          RCA
                          <input
                            type="text"
                            value={resolutionRca}
                            onChange={(e) => setResolutionRca(e.target.value)}
                            placeholder="Root cause"
                            readOnly={resolutionUpdatesLocked}
                            disabled={resolutionUpdatesLocked}
                            className={`${fieldInputClasses}${
                              resolutionUpdatesLocked ? ` ${resolutionFieldDisabledClasses}` : ''
                            }`}
                          />
                        </label>
                        <label className="flex flex-col text-sm text-gray-700">
                          PA (Preventive Action)
                          <input
                            type="text"
                            value={resolutionPa}
                            onChange={(e) => setResolutionPa(e.target.value)}
                            placeholder="Preventive action"
                            readOnly={resolutionUpdatesLocked}
                            disabled={resolutionUpdatesLocked}
                            className={`${fieldInputClasses}${
                              resolutionUpdatesLocked ? ` ${resolutionFieldDisabledClasses}` : ''
                            }`}
                          />
                        </label>
                        <label className="flex flex-col text-sm text-gray-700">
                          CA (Corrective Action)
                          <input
                            type="text"
                            value={resolutionCa}
                            onChange={(e) => setResolutionCa(e.target.value)}
                            placeholder="Corrective action"
                            readOnly={resolutionUpdatesLocked}
                            disabled={resolutionUpdatesLocked}
                            className={`${fieldInputClasses}${
                              resolutionUpdatesLocked ? ` ${resolutionFieldDisabledClasses}` : ''
                            }`}
                          />
                        </label>
                      </div>
                    </div>
                  ) : null}

                  <div className="pt-6 flex justify-start gap-5">
                    <button
                      type="submit"
                      disabled={
                        isCreatingComplaint || isUpdatingComplaint || isSavingComplaintComment
                      }
                      className="inline-flex min-w-[140px] items-center justify-center rounded-lg bg-brand px-8 py-2.5 text-sm font-semibold text-white shadow-sm"
                    >
                      {(isCreatingComplaint || isUpdatingComplaint || isSavingComplaintComment)
                        ? 'Saving...'
                        : (editingComplaintId ? 'Update' : 'Save')}
                    </button>
                    <button
    type="button"
    onClick={handleBackToList}
    className="inline-flex min-w-[140px] items-center justify-center rounded-lg border border-brand bg-white px-8 py-2.5 text-sm font-semibold text-brand"
  >
    Cancel
  </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {viewMode === 'detail' && selectedComplaint && (
          <div className="fixed inset-0 z-30 flex justify-end bg-black/30">
            <div className="flex h-full w-full max-w-xl flex-col gap-4 overflow-y-auto bg-white p-4 shadow-2xl sm:p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-base font-semibold text-gray-900">
                    {isComplaintDetailItsm
                      ? 'ITSM Complaint Information'
                      : 'CTM Complaint Information'}
                  </h2>
                  <span className="inline-flex items-center rounded-full bg-brand px-2 py-1 text-xs font-bold text-white">
                   {selectedComplaint?.raw.work_priority_name ?? 'N/A'}
                  </span>
                  {canEmployeeSubmitReview ? (
                    <button
                      type="button"
                      onClick={handleOpenReviewModal}
                      className="inline-flex items-center rounded-full border border-brand bg-white px-3 py-1 text-xs font-semibold text-brand shadow-sm hover:bg-[#faf8f5]"
                    >
                      Add Review +
                    </button>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="text-3xl text-red-700 hover:text-red-800"
                  onClick={handleBackToList}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              {detailComplaintRating?.rating >= 1 ? (
                <div className="rounded-lg border border-amber-200/80 bg-amber-50/50 px-4 py-3 text-sm">
                  <p className="mb-2 font-semibold text-gray-900">Rating</p>
                  <ComplaintStarRating value={detailComplaintRating.rating} readOnly size="md" />
                  {detailComplaintRating.feedback ? (
                    <p className="mt-2 whitespace-pre-wrap text-gray-700">
                      {detailComplaintRating.feedback}
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
                <div>
                  <p className="text-gray-500">Complaint ID</p>
                  <p className="font-medium text-gray-900">
                    {String(
                      selectedComplaint.acknowledgementNumber ||
                        selectedComplaint.raw?.acknowledgement_number ||
                        ''
                    ).trim() || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Location</p>
                  <p className="font-medium text-gray-900">{selectedComplaint.location}</p>
                </div>
                <div>
                  <p className="text-gray-500">Reported Date</p>
                  <p className="font-medium text-gray-900">{selectedComplaint.reportedDate}</p>
                </div>
                <div>
                  <p className="text-gray-500">Type</p>
                  <p className="font-medium text-gray-900">
                    {selectedComplaint.complaintType || '—'}
                  </p>
                </div>
                {isComplaintDetailItsm && (
                  <>
                    <div>
                      <p className="text-gray-500">Date of Incident</p>
                      <p className="font-medium text-gray-900">
                        {selectedComplaint.raw?.date_of_incident
                          ? new Date(
                              selectedComplaint.raw.date_of_incident
                            ).toLocaleDateString()
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Issue Type</p>
                      <p className="font-medium text-gray-900">
                        {selectedComplaint.raw?.issue_type_name ||
                          selectedComplaint.raw?.noc_issue_type_name ||
                          selectedComplaint.raw?.complaint_sub_type ||
                          '—'}
                      </p>
                    </div>
                  </>
                )}
                {!isComplaintDetailItsm && (
                  <>
                    <div>
                      <p className="text-gray-500">Quantity</p>
                      <p className="font-medium text-gray-900">
                        {formatComplaintQuantity(selectedComplaint.raw?.quantity)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">UOM</p>
                      <p className="font-medium text-gray-900">
                        {formatComplaintUom(selectedComplaint.raw?.uom)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Vehicle storage temperature</p>
                      <p className="font-medium text-gray-900">
                        {selectedComplaint.raw?.vehicle_storage_temperature ??
                          selectedComplaint.raw?.vehicleStorageTemperature ??
                          '—'}
                      </p>
                    </div>
                  </>
                )}
                <div className={isEmployeeArea ? 'sm:col-span-2' : ''}>
                  <p className="text-gray-500">Status</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${getStatusClasses(
                        selectedComplaint.status
                      )}`}
                    >
                      {selectedComplaint.status}
                    </span>
                    {isEmployeeArea ? (
                      <TrackingStatusButton onClick={() => setIsTrackingOpen(true)} />
                    ) : null}
                  </div>
                </div>
                <div>
                  <p className="text-gray-500">TAT</p>
                  <p className="font-medium text-gray-900">
                    {complaintTatDisplayValue(selectedComplaint.raw)}
                  </p>
                </div>
                   <div>
                    <p className="text-gray-500">Raised By</p>
                    <p className="font-medium text-gray-900">{selectedComplaint.raisedBy}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Reporting Supervisor</p>
                    <p className="font-medium text-gray-900">
                      {selectedComplaint.managerOnDuty}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Accountable Department</p>
                    <p className="font-medium text-gray-900">
                      {selectedComplaint.raw?.department_name || '—'}
                    </p>
                  </div>
                  {!isComplaintDetailItsm && (
                    <div>
                      <p className="text-gray-500">Issue Type</p>
                      <p className="font-medium text-gray-900">
                        {selectedComplaint.raw?.issue_type_name ||
                          '—'}
                      </p>
                    </div>
                  )}
              </div>

              <div className="text-sm">
                <p className="mb-1 font-semibold text-gray-900">
                  {isComplaintDetailItsm ? 'Issue Description' : 'Remarks'}
                </p>
                <p className="text-gray-600">
                  {(isComplaintDetailItsm ? (selectedComplaint.raw?.issue_description || selectedComplaint.issueDescription) : selectedComplaint.issueDescription)?.trim() ? (isComplaintDetailItsm ? (selectedComplaint.raw?.issue_description || selectedComplaint.issueDescription) : selectedComplaint.issueDescription) : '—'}
                </p>
              </div>

              <div className="border-t border-gray-200 pt-3 text-sm">
                <p className="mb-2 font-semibold text-gray-900">
                  Attachments
                  {Array.isArray(selectedComplaint.viewAttachments) &&
                  selectedComplaint.viewAttachments.length > 0
                    ? ` (${selectedComplaint.viewAttachments.length})`
                    : ''}
                </p>
                <ComplaintDetailAttachmentGallery
                  items={
                    selectedComplaint.viewAttachments?.length
                      ? selectedComplaint.viewAttachments
                      : parseExistingAttachmentsFromRaw(selectedComplaint.raw || {})
                  }
                />
              </div>

              {!isComplaintDetailItsm && (
              <details className="border-t border-gray-200 pt-3 text-sm" open>
                <summary className="cursor-pointer select-none font-semibold text-gray-900">
                  Product Information
                </summary>
                <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                  <div>
                    <p className="text-gray-500">Product Name</p>
                    <p className="font-medium text-gray-900">
                      {selectedComplaint.raw?.complaint_product_name ||
                        selectedComplaint.raw?.product_name ||
                        selectedComplaint.raw?.productName ||
                        '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Product Category</p>
                    <p className="font-medium text-gray-900">
                      {selectedComplaint.raw?.product_category_name ||
                        selectedComplaint.raw?.productCategoryName ||
                        '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Product Sub Category</p>
                    <p className="font-medium text-gray-900">
                      {selectedComplaint.raw?.product_subcategory_name ||
                        selectedComplaint.raw?.productSubcategoryName ||
                        '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Batch No. (As Per Actual)</p>
                    <p className="font-medium text-gray-900">
                      {selectedComplaint.raw?.batch_number || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">MFG Date</p>
                    <p className="font-medium text-gray-900">
                      {selectedComplaint.raw?.mfg_date || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Use Before / Best Before</p>
                    <p className="font-medium text-gray-900">
                      {selectedComplaint.raw?.use_best_before || '—'}
                    </p>
                  </div>
                </div>
              </details>
              )}

              <details className="border-t border-gray-200 pt-3 text-sm" open>
                <summary className="cursor-pointer select-none font-semibold text-gray-900">
                  Complaint Information
                </summary>
                    {(() => {
                    const { rca, pa, ca } = latestRcaPaCaFromRaw(selectedComplaint.raw);
                    const paClassName =
                      pa && /review/i.test(String(pa))
                        ? 'font-medium text-amber-700'
                        : 'font-medium text-gray-900';
                    return (
                  <div className="grid grid-cols-1 gap-x-6 gap-y-3 py-3 sm:grid-cols-2">
                    <div>
                      <p className="text-gray-500">RCA</p>
                      <p className="font-medium text-gray-900">{rca ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">PA (Preventive Action)</p>
                      <p className={paClassName}>{pa ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">CA (Corrective Action)</p>
                      <p className="font-medium text-gray-900">{ca ?? '—'}</p>
                    </div>
                  </div>
                    );
                  })()}
                <div className="mt-3 space-y-6">
                  <div>
                    <p className="mb-2 font-semibold text-gray-900">Comments</p>
                    {complaintCommentsFromRaw(detailPanelRaw || selectedComplaint.raw).length ===
                    0 ? (
                      <p className="text-sm text-gray-500">No comments yet.</p>
                    ) : (
                      <ul className="max-h-64 space-y-3 overflow-y-auto pr-1">
                        {complaintCommentsFromRaw(detailPanelRaw || selectedComplaint.raw).map(
                          (c) => {
                            const imgUrl = c?.image_url
                              ? resolveAssetUrl(String(c.image_url))
                              : '';
                            const assignedNames = Array.isArray(c?.assigned_users)
                              ? c.assigned_users
                                  .map((u) => u?.name)
                                  .filter(Boolean)
                                  .join(', ')
                              : '';
                            return (
                              <li
                                key={c.id ?? `${c.created_at}-${c.comment}`}
                                className="rounded-lg border border-gray-100 bg-[#faf8f5] p-3"
                              >
                                <div className="flex flex-wrap items-baseline justify-between gap-2 text-xs text-gray-500">
                                  <span className="font-medium text-gray-800">
                                    {c.account_name || '—'}
                                    {c.commented_by_type ? (
                                      <span className="font-normal text-gray-500">
                                        {' '}
                                        · {c.commented_by_type}
                                      </span>
                                    ) : null}
                                  </span>
                                  <span>{formatReportedDateTime(c.created_at)}</span>
                                </div>
                                {c.status ? (
                                  <p className="mt-1 text-xs text-gray-600">
                                    Status:{' '}
                                    <span className="font-medium">
                                      {formatComplaintStatusLabel(c.status)}
                                    </span>
                                  </p>
                                ) : null}
                                {assignedNames ? (
                                  <p className="mt-0.5 text-xs text-gray-600">
                                    Assigned: {assignedNames}
                                  </p>
                                ) : null}
                                <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800">
                                  {c.comment || '—'}
                                </p>
                                {imgUrl ? (
                                  <div className="mt-2 h-24 w-24 overflow-hidden rounded-md border border-gray-200 bg-black/5">
                                    <AuthorizedMedia
                                      src={imgUrl}
                                      alt=""
                                      className="h-full w-full object-cover"
                                      onError={(ev) => {
                                        ev.currentTarget.style.display = 'none';
                                      }}
                                    />
                                  </div>
                                ) : null}
                              </li>
                            );
                          }
                        )}
                      </ul>
                    )}
                  </div>

                  <form
                    onSubmit={handleDetailComplaintReplySubmit}
                    className="space-y-3 border-t border-gray-200 pt-4"
                  >
                    <p className="font-semibold text-gray-900">Reply</p>
                    {loggedInAccountId && isLoadingCommentAuthorType ? (
                      <p className="text-xs text-gray-500">Refreshing account…</p>
                    ) : null}
                    <label className="block">
                      <textarea
                        value={detailReplyComment}
                        onChange={(e) => setDetailReplyComment(e.target.value)}
                        rows={3}
                        placeholder="Write your reply…"
                        className="mt-1 w-full rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm text-gray-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                      />
                    </label>
                    <button
                      type="submit"
                      disabled={isSavingComplaintComment}
                      className="inline-flex min-w-[120px] items-center justify-center rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                    >
                      {isSavingComplaintComment ? 'Posting…' : 'Post reply'}
                    </button>
                  </form>

              
                </div>
              </details>
            </div>

            {isEmployeeArea ? (
              <ComplaintTrackingModal
                complaintId={selectedComplaint?.numericId ?? detailPanelRaw?.id}
                open={isTrackingOpen}
                onClose={resetTrackingModal}
              />
            ) : null}

            {isReviewModalOpen ? (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
                role="dialog"
                aria-modal="true"
                aria-label="Complaint review"
                onClick={() => setIsReviewModalOpen(false)}
              >
                <form
                  className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl sm:p-6"
                  onClick={(e) => e.stopPropagation()}
                  onSubmit={handleReviewSubmit}
                >
                  <h3 className="text-lg font-semibold text-brand">Add Review and Rating</h3>
                  <div className="mt-4">
                    <span className="mb-2 block text-sm font-medium text-gray-700">Rating</span>
                    <ComplaintStarRating value={reviewRating} onChange={setReviewRating} />
                  </div>
                  <label className="mt-4 block">
                    <span className="mb-1 block text-sm font-medium text-gray-700">Feedback</span>
                    <textarea
                      value={reviewFeedback}
                      onChange={(e) => setReviewFeedback(e.target.value)}
                      rows={4}
                      placeholder="Share your experience…"
                      className="w-full rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm text-gray-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                    />
                  </label>
                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setIsReviewModalOpen(false)}
                      className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSavingComplaintRating || reviewRating < 1}
                      className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                    >
                      {isSavingComplaintRating ? 'Submitting…' : 'Submit review'}
                    </button>
                  </div>
                </form>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export function EmployeeComplaintsPage() {
  return <ComplaintsPage />;
}

export function AdminComplaintsPage() {
  return <ComplaintsPage />;
}

export default ComplaintsPage;

